import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import cloneDeep from "lodash/cloneDeep";
import { useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Popover,
  Space,
  Typography,
  message,
} from "antd";

import {
  fetchsearchResultsFromMaterialDB,
  getMaterialPropertyDetailById,
  fetchsearchResultsFromSamDB,
  resetMaterailSearchReucer,
  fetchAutorTypes,
  fetchMaterialTypes,
  fetchMaterialColumns,
  getSamPropertyDetailById,
  fetchsearchResultsFromFiberDB,
  fetchAverageFiberResultsFromCalculator,
  getFiberPropertyDetailById,
  fetchsearchResultsFromLabvantageAPi,
  fetchCalulatorApiResult,
  resetCalulatorApiResult,
  getCompressionData,
  updateSearchKey,
  resetCalulatorApiResultToInintal,
} from "../../axios/index";

import DataGrid from "../tables/dataGrid";
import BarChart from "../charts/lineChart";
import loader from "../../loader.gif";
import "./index.css";
import Graphs from "./graphs";

// SAM metadata provenance: local helpers; no new application module.
const samCalculatorMetadataKeys = ["calculatorName", "inputParameters", "calculatorVersion"];
const samMetadataKeys = ["source", "author", "testMethod", "creationDate", "repCount", "sampleId", "requestDate", "description"];
const samClone = (value) => {
  if (value instanceof Date) return new Date(value.getTime());
  if (Array.isArray(value)) return value.map(samClone);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, samClone(item)]));
  return value;
};
const samObject = (value) => {
  if (typeof value === "string") {
    try { value = JSON.parse(value); } catch { return {}; }
  }
  return value && typeof value === "object" && !Array.isArray(value) ? samClone(value) : {};
};
const samMissing = (value) => value === undefined || value === null || value === "" || value === "-";
const samNormalizeKey = (value) => String(value || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
const canonicalSamMetadata = (value) => {
  const result = samObject(value);
  const aliases = {
    calculatorName: ["calculatorname", "calcname"],
    inputParameters: ["inputparameters"],
    calculatorVersion: ["calculatorversion"],
    source: ["source"], author: ["author", "createdby", "requestorname", "requestor"],
    testMethod: ["testmethod", "testmethodname", "methodname", "methodid", "method"],
    creationDate: ["creationdate", "completeddt", "completeddate", "completiondate", "createddate", "createdt"],
    repCount: ["repcount", "replicateid", "replicatecount"],
    sampleId: ["sampleid", "samplekey", "keyid1"],
    requestDate: ["requestdate", "requestdt", "requesteddate"],
    description: ["description", "sampledescription", "productdescription", "materialdescription"],
  };
  const entries = Object.entries(result);
  Object.entries(aliases).forEach(([key, names]) => {
    if (!samMissing(result[key]) || (Array.isArray(result._manualClears) && result._manualClears.includes(key))) return;
    for (const name of names) {
      const match = entries.find(([candidate, item]) => samNormalizeKey(candidate) === name && !samMissing(item));
      if (match) { result[key] = samClone(match[1]); break; }
    }
  });
  return result;
};
const normalizeSamSource = (value) => {
  const result = samObject(value);
  result.metaData = canonicalSamMetadata(result.metaData);
  const alternate = canonicalSamMetadata(result.metadata);
  Object.entries(alternate).forEach(([key, value]) => {
    if (samMissing(result.metaData[key]) && !samMetadataWasCleared(result.metaData, key)) result.metaData[key] = samClone(value);
  });
  // Some saved sources keep dates/metadata at the source root. Mirror known
  // keys for display without removing their original representation.
  const rootMetadata = canonicalSamMetadata(result);
  [...samMetadataKeys, ...samCalculatorMetadataKeys].forEach((key) => {
    if (samMissing(result.metaData[key]) && !samMetadataWasCleared(result.metaData, key) && !samMissing(rootMetadata[key])) result.metaData[key] = samClone(rootMetadata[key]);
  });
  return result;
};
const samMetadataFromRow = (row) => {
  const value = samObject(row);
  const result = normalizeSamSource(value.dataSource).metaData;
  [value.metadata, value.metaData].forEach((metadata) => {
    const normalized = canonicalSamMetadata(metadata);
    Object.entries(normalized).forEach(([key, item]) => {
      if (!samMissing(item) || samMetadataWasCleared(normalized, key)) result[key] = samClone(item);
    });
  });
  const direct = canonicalSamMetadata(value);
  [...samMetadataKeys, ...samCalculatorMetadataKeys].forEach((key) => {
    if (!samMissing(direct[key])) result[key] = samClone(direct[key]);
  });
  return result;
};
// Some calculator versions put provenance on the output row itself.
const samSourceFromOutput = (row) => {
  const source = normalizeSamSource(row?.dataSource);
  Object.entries(samMetadataFromRow(row)).forEach(([name, value]) => {
    if (samMissing(source.metaData[name]) && !samMetadataWasCleared(source.metaData, name) && !samMissing(value))
      source.metaData[name] = samClone(value);
  });
  return source;
};
const samMetadataWasCleared = (metadata, key) => Array.isArray(metadata?._manualClears) && metadata._manualClears.includes(key);
const trackSamMetadataClear = (metadata, key, value) => {
  if (!samMetadataKeys.includes(key)) return metadata;
  const clears = new Set(Array.isArray(metadata._manualClears) ? metadata._manualClears : []);
  if (value === "" || value === null) clears.add(key); else clears.delete(key);
  if (clears.size) metadata._manualClears = [...clears]; else delete metadata._manualClears;
  return metadata;
};
const commonSamMetadata = (sources) => {
  const metadata = (sources || []).map((source) => normalizeSamSource(source).metaData);
  const result = {};
  if (!metadata.length) return result;
  samMetadataKeys.forEach((key) => {
    // Constants and unannotated inputs must not hide actual selected-record
    // metadata. Keep conflicting values separate in inputMetaData.
    const available = metadata.filter((item) => !samMissing(item[key]));
    const first = available[0]?.[key];
    if (available.length && available.every((item) => JSON.stringify(item[key]) === JSON.stringify(first))) result[key] = samClone(first);
  });
  return result;
};
// End SAM metadata provenance.

// Merge sparse API metadata without discarding existing values. Explicit user
// clears are applied separately, so an empty input stays deliberately empty.
const mergeMetadataSource = (current, incoming) => {
  const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
  const blocked = new Set(["__proto__", "constructor", "prototype"]);
  const merge = (left, right) => {
    const result = cloneDeep(isRecord(left) ? left : {});
    Object.entries(isRecord(right) ? right : {}).forEach(([key, value]) => {
      if (blocked.has(key) || value === undefined || value === null || value === "" || value === "-") return;
      result[key] = isRecord(value) ? merge(result[key], value) : cloneDeep(value);
    });
    return result;
  };
  const result = merge(normalizeSamSource(current), normalizeSamSource(incoming));
  if (!isRecord(result.metaData)) result.metaData = {};
  return result;
};

// One immutable metadata snapshot per calculator invocation. A manual draft
// must never overwrite a newly selected LabVantage record on the same tab.
const snapshotCalculatorInputMetadata = (selected, manual) => {
  const result = cloneDeep(selected || {});
  Object.entries(manual || {}).forEach(([tabKey, metadata]) => {
    if (!result[tabKey] || String(result[tabKey].source).toLowerCase() === "manual")
      result[tabKey] = { ...result[tabKey], ...cloneDeep(metadata) };
  });
  return result;
};

const DRY_GEL_DENSITY_QUERY_ID = "TTM-00439";

const isDevelopmentBuild =
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV === "development";

const normalizeSearchKey = (key) =>
  String(key || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

const normalizeSearchText = (value) =>
  String(value ?? "").trim().toLowerCase();

const hasSearchValue = (value) => {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    return trimmedValue !== "" && trimmedValue !== "-";
  }

  return true;
};

const getLabVantageTabKey = (tab) =>
  `${tab?.queryId || tab?.labVantageApi || ""}_${
    tab?.dbColumnName || ""
  }`;

const isDryGelDensityQuery = (queryId) =>
  normalizeSearchKey(queryId).includes(
    normalizeSearchKey(DRY_GEL_DENSITY_QUERY_ID)
  );

const isSamDensityKey = (key) =>
  normalizeSearchKey(key).includes("samdensity");

const CALCULATOR_INPUT_DEFAULTS = [
  {
    matchKeys: ["fluiddensity", "testfluiddensity"],
    value: 1,
  },
  {
    matchKeys: [
      "fluidtemperature",
      "testfluidtemperature",
      "testfluidtemprature",
      "testtemperature",
      "temperature",
    ],
    value: 21.85,
  },
  {
    matchKeys: [
      "molarconcentrationofsalt",
      "saltmolarconcentration",
      "molarsaltconcentration",
      "saltconcentration",
    ],
    value: 0.155,
  },
];

const isSamDensityCalculatorField = (item) => {
  const normalizedColumn = normalizeSearchKey(
    item?.dbColumnName
  );

  const normalizedLabel = normalizeSearchKey(
    item?.fieldLabel
  );

  return (
    normalizedColumn.includes("samdensity") ||
    normalizedLabel.includes("samdensity")
  );
};

const getAdvanceSearchFieldId = (
  key,
  fallback = "field"
) => {
  const normalizedKey = normalizeSearchKey(key);

  return `advance-search-${normalizedKey || fallback}`;
};

const getCanonicalLabVantageKey = (key) => {
  const normalizedKey = normalizeSearchKey(key);

  if (
    normalizedKey === "sampleid" ||
    normalizedKey === "keyid" ||
    normalizedKey === "keyid1"
  ) {
    return "sampleId";
  }

  if (
    normalizedKey === "productdescription" ||
    normalizedKey === "productdesc" ||
    normalizedKey === "description"
  ) {
    return "productDescription";
  }

  return key;
};

const normalizeLabVantageSearchParams = (params) => {
  const normalizedParams = {};

  Object.entries(params || {}).forEach(([key, value]) => {
    if (!hasSearchValue(value)) {
      return;
    }

    const canonicalKey = getCanonicalLabVantageKey(key);
    const cleanedValue =
      typeof value === "string" ? value.trim() : value;

    if (
      normalizedParams[canonicalKey] === undefined ||
      !hasSearchValue(normalizedParams[canonicalKey])
    ) {
      normalizedParams[canonicalKey] = cleanedValue;
    }
  });

  return normalizedParams;
};

const getNestedRowRecords = (
  value,
  visited = new Set()
) => {
  if (
    !value ||
    typeof value !== "object" ||
    visited.has(value)
  ) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) =>
      getNestedRowRecords(item, visited)
    );
  }

  visited.add(value);

  const records = [value];

  [
    "data",
    "row",
    "record",
    "item",
    "result",
    "dataSet",
    "dataset",
    "rawData",
    "responseData",
    "rows",
    "results",
    "items",
  ].forEach((key) => {
    const nestedValue = value[key];

    if (
      nestedValue &&
      typeof nestedValue === "object"
    ) {
      records.push(
        ...getNestedRowRecords(nestedValue, visited)
      );
    }
  });

  return records;
};

const getRowValueByAliases = (row, aliases) => {
  const records = getNestedRowRecords(row);

  for (const record of records) {
    for (const alias of aliases) {
      const normalizedAlias = normalizeSearchKey(alias);

      const matchingKey = Object.keys(record).find(
        (key) =>
          normalizeSearchKey(key) === normalizedAlias
      );

      if (
        matchingKey !== undefined &&
        hasSearchValue(record[matchingKey])
      ) {
        return record[matchingKey];
      }
    }
  }

  return undefined;
};

const filterLabVantageRows = (
  rows,
  searchParams
) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  const sampleId = normalizeSearchText(
    searchParams?.sampleId
  );

  const productDescription = normalizeSearchText(
    searchParams?.productDescription
  );

  if (!sampleId && !productDescription) {
    return rows;
  }

  return rows.filter((row) => {
    if (sampleId) {
      const rawRowSampleId = getRowValueByAliases(row, [
        "sampleId",
        "sampleid",
        "Sample ID",
        "SampleID",
        "sample_id",
        "keyid1",
      ]);

      if (
        rawRowSampleId === undefined ||
        normalizeSearchText(rawRowSampleId) !== sampleId
      ) {
        return false;
      }
    }

    if (productDescription) {
      const rawRowDescription = getRowValueByAliases(
        row,
        [
"productDescription",
          "productdescription",
          "Product Description",
          "productDesc",
          "sampleDescription",
          "samDescription",
          "sampledesc",
          "materialDescription",
          "productName",
          "keyid2",
          "description",
          "Description",
        ]
      );

      if (
        rawRowDescription === undefined ||
        !normalizeSearchText(rawRowDescription).includes(
          productDescription
        )
      ) {
        return false;
      }
    }

    return true;
  });
};

function AdvanceSearch(props) {
  const dispatch = useDispatch();
  const urlParams = useParams();

  const [
    submittedLabVantageSearchParams,
    setSubmittedLabVantageSearchParams,
  ] = useState({});

  const userInfo = useSelector(
    (state) => state.userInfo
  );

  const tableData = useSelector(
    (state) => state.materialSearch
  );

  const queryIdForSelectedPropertyRow = useSelector(
    (state) => state.QueryIdForSelectedProperty
  );

  const calulatorApiResultData = useSelector(
    (state) => state.CalulatorApiResult
  );

  const defaultDropdownOptions = useSelector(
    (state) => state.materialSearch.metaData
  );

  const columnsList = useSelector(
    (state) =>
      state.materialSearch.metaData.materialColumnTypes
  );

  const defaultMetaDataFieldsList = useSelector(
    (state) => state.defaultMetaDataFields
  );

  const others = useSelector(
    (state) => state.materialSearch.others
  );

  const [selectedColumns, setSelectedColumns] =
    useState({});

  const [
    advanceSearchFieldValues,
    setAdvanceSearchFieldValues,
  ] = useState({});

  const [
    columnChooserVisible,
    setColumnChooserVisible,
  ] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] =
    useState([]);

  const [selectedRows, setSelectedRows] =
    useState([]);

  const [searchType, setSearchType] = useState(
    props.modal.module
  );

  const [fiberAvgError, setFiberAvgError] = useState({
    code: "",
    response: "",
    type: "",
  });

  const [showLabvantageInputs] = useState(false);

  const [selectedApiTab, setSelectedApiTab] =
    useState(null);

  const [labVantageActiveTab, setLabVantageActiveTab] =
    useState({});

  const [labVantageInputs, setLabVantageInputs] =
    useState({});

  const [labVantageTabValues, setLabVantageTabValues] =
    useState({});

  const [labVantageTabErrors, setLabVantageTabErrors] =
    useState({});

  const [lastLabvSelection, setLastLabvSelection] =
    useState(null);

  const [
    showLabVantageCalculatorButton,
  ] = useState(false);

  const [autoCallCalulator, setAutoCallCalulator] =
    useState(false);

  const [otherSourceType, setOtherSourceType] =
    useState("labvantage");

  const [
    isLabvantageSearching,
    setIsLabvantageSearching,
  ] = useState(false);

  const [
    isCalculatorRunning,
    setIsCalculatorRunning,
  ] = useState(false);

  const [
    calculatorRunError,
    setCalculatorRunError,
  ] = useState("");

  const [
    labVantageTabSnapshots,
    setLabVantageTabSnapshots,
  ] = useState({});

  const calculatorRequestIdRef = useRef(0);
  const calculatorRequestProvenanceRef = useRef(null);
  const calculatorRequestActiveRef = useRef(false);
  const calculatorRunTimeoutRef = useRef(null);
  const calculatorMountedRef = useRef(true);

  const samDensityInitializedFor = useRef(null);
  const [manualMetadataByTab, setManualMetadataByTab] = useState({});
  const manualMetadataRef = useRef({});
  const selectedInputMetadataRef = useRef({});
  const selectionRecordRef = useRef(null);

  const activeLabVantageTab =
    selectedApiTab || labVantageActiveTab;

  const calculatorDetail =
    queryIdForSelectedPropertyRow?.data
      ?.calculatorDetail;

  const currentCalculatorName =
    calculatorDetail?.calculatorName;

  const isSampleBasedCalculator =
    currentCalculatorName === "GelProp_C" ||
    currentCalculatorName === "CalcDiff";

  // Hooke's Law Constant & Gel Osmotic Activity (GelProp_A) populate the
  // multi-row "Inputs from other sources" value table immediately on row
  // selection, same as before - no "Use Selection" button confirmation.
  // Avg. Dry Gel Radius (SAMRadius) uses the same immediate multi-field
  // population path (screen mesh values keyed off the selected sample).
  // Load Coefficients (GelProp_B) shares the same multi-row AUL structure
  // as GelProp_A, so it needs the same immediate population path.
  const isHookesLawCalculator =
    currentCalculatorName === "GelProp_A" ||
    currentCalculatorName === "GelProp_B" ||
    currentCalculatorName === "SAMRadius";

  // Hooke's Law Constant, Gel Osmotic Activity (GelProp_A) and Load
  // Coefficients A/B (GelProp_B) must only accept a numeric row Value into
  // the active tab - never a Description or other non-numeric field.
  const requiresNumericTabValidation =
    currentCalculatorName === "GelProp_A" ||
    currentCalculatorName === "GelProp_B";

  const hasMeaningfulValue = (value) => {
    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();

      return (
        trimmedValue !== "" &&
        trimmedValue !== "-"
      );
    }

    return true;
  };

  const getEffectiveLabvFieldKey = () => {
    if (isSampleBasedCalculator) {
      return "Times/TimedAUL";
    }

    if (
      otherSourceType !== "others" &&
      props?.advancedSearchMetaData?.key
    ) {
      return props.advancedSearchMetaData.key;
    }

    return activeLabVantageTab?.dbColumnName;
  };

  const getCurrentTabValue = (tab) => {
    const tabKey = getLabVantageTabKey(tab);

    if (
      Object.prototype.hasOwnProperty.call(
        labVantageTabValues,
        tabKey
      )
    ) {
      return labVantageTabValues[tabKey];
    }

    return tab?.dbColumnName
      ? labVantageInputs[tab.dbColumnName]
      : undefined;
  };

  const formatTabValue = (value) => {
    if (!hasMeaningfulValue(value)) {
      return "-";
    }

    if (typeof value === "object") {
      return Object.entries(value)
        .map(
          ([key, itemValue]) =>
            `${key}: ${itemValue}`
        )
        .join(", ");
    }

    return String(value);
  };

  const getCurrentSamPropertyValue = (fieldKeys) => {
    const keys = Array.isArray(fieldKeys)
      ? fieldKeys
      : [fieldKeys];

    const rows =
      props.moduleDefaultFieldDataSet?.sam
        ?.propertyData || [];

    for (const row of rows) {
      for (const key of keys) {
        const value = row?.[key]?.value;

        if (
          value !== undefined &&
          value !== null &&
          value !== "" &&
          value !== "-"
        ) {
          return value;
        }
      }
    }

    return undefined;
  };

  const applyCalculatorDefaultValues = (
    calculatorName,
    values,
    calculatorItems = []
  ) => {
    const nextValues = { ...values };

    const normalizeValue = (value) =>
      String(value ?? "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .toLowerCase();

    calculatorItems.forEach((item) => {
      const key = item?.dbColumnName;

      if (!key) {
        return;
      }

      const currentValue = nextValues[key];

      if (
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== ""
      ) {
        return;
      }

      if (
        item.fieldDefaultValue !== null &&
        item.fieldDefaultValue !== undefined &&
        item.fieldDefaultValue !== ""
      ) {
        nextValues[key] = item.fieldDefaultValue;
        return;
      }

      const haystack = `${normalizeValue(
        item.dbColumnName
      )} ${normalizeValue(item.fieldLabel)}`;

      const match = CALCULATOR_INPUT_DEFAULTS.find(
        (entry) =>
          entry.matchKeys.some((matchKey) =>
            haystack.includes(normalizeValue(matchKey))
          )
      );

      if (match) {
        nextValues[key] = match.value;
      }
    });

    if (calculatorName !== "CalcDiff") {
      return nextValues;
    }

    const gelRadius = getCurrentSamPropertyValue(
      "solidsDryGelRadius"
    );

    const gelDensity = getCurrentSamPropertyValue(
      "solidsDryGelDensity"
    );

    const samCapacity = getCurrentSamPropertyValue([
      "maxCapacity",
      "0.3psiGBP",
    ]);

    if (gelRadius !== undefined) {
      nextValues.GelRadius = gelRadius;
    }

    if (gelDensity !== undefined) {
      nextValues.GelDensity = gelDensity;
    }

    if (samCapacity !== undefined) {
      nextValues.VacuumAUL = samCapacity;
    }

    return nextValues;
  };

  const sanitizeNumberInputValue= (value) => {
    if (
      value === undefined ||
      value === null ||
      value === "-"
    ) {
      return "";
    }

    if (typeof value === "number") {
      return Number.isNaN(value) ? "" : value;
    }

    const text = String(value).trim();

    if (!text || text === "-") {
      return "";
    }

    const parsed = Number(text);

    return Number.isNaN(parsed) ? "" : parsed;
  };

  const sanitizeRequestParams = (params) => {
    const cleaned = {};

    Object.keys(params || {}).forEach((key) => {
      const value = params[key];

      if (value === undefined || value === null) {
        return;
      }

      if (
        key === "Times/TimedAUL" &&
        typeof value === "number"
      ) {
        return;
      }

      if (typeof value === "string") {
        const trimmedValue = value.trim();

        if (!trimmedValue || trimmedValue === "-") {
          return;
        }

        cleaned[key] = trimmedValue;
        return;
      }

      if (typeof value === "number") {
        if (!Number.isNaN(value)) {
          cleaned[key] = value;
        }

        return;
      }

      cleaned[key] = value;
    });

    return cleaned;
  };

  const isLabVantageSampleKey = (value) => {
    if (!hasMeaningfulValue(value)) {
      return false;
    }

    const numericValue = Number(String(value).trim());

    return (
      Number.isFinite(numericValue) &&
      Number.isInteger(numericValue)
    );
  };

  const getSelectedLabvSampleKey = (record) => {
    if (!record) {
      return null;
    }

    const candidates = [
      record.keyid1,
      record.keyId1,
      record.sampleId,
      record.sampleid,
      record.SampleID,
      record["Sample ID"],
      record.sample_id,
      record.sampleKey,
      record.samplekey,
    ];

    const sampleKey = candidates.find(
      isLabVantageSampleKey
    );

    return sampleKey !== undefined
      ? String(sampleKey).trim()
      : null;
  };

  const LABVANTAGE_SAMPLE_ID_PATTERN =
    /^S\d{2}-\d{6}$/i;

  const isPlausibleLabVantageSampleIdValue = (
    value
  ) => {
    if (!hasMeaningfulValue(value)) {
      return false;
    }

    return LABVANTAGE_SAMPLE_ID_PATTERN.test(
      String(value).trim()
    );
  };

  const getSelectedLabvSampleId = (record) => {
    if (!record) {
      return null;
    }

    const candidates = [
      record.sampleId,
      record.sampleid,
      record.SampleID,
      record["Sample ID"],
      record.sample_id,
      record.sampleKey,
      record.samplekey,
    ];

    const namedMatch = candidates.find(
      isPlausibleLabVantageSampleIdValue
    );

    if (namedMatch !== undefined) {
      return String(namedMatch).trim();
    }

    const anyMatch = Object.values(record).find(
      isPlausibleLabVantageSampleIdValue
    );

    return anyMatch !== undefined
      ? String(anyMatch).trim()
      : null;
  };

  const getSelectedLabvDescription = (record) => {
    if (!record) {
      return null;
    }

    const descriptionKey = Object.keys(record).find(
      (key) =>
        String(key)
          .replace(/[^a-zA-Z0-9]/g, "")
          .toLowerCase()
          .includes("desc")
    );

    if (
      descriptionKey &&
      hasMeaningfulValue(record[descriptionKey])
    ) {
      return String(record[descriptionKey]).trim();
    }

    return null;
  };

  const getLabvSampleKeyForMeasuredValue = (
    measuredValue
  ) => {
    const numericMeasuredValue = Number(measuredValue);

    if (!Number.isFinite(numericMeasuredValue)) {
      return null;
    }

    const rows = Array.isArray(tableData.data?.rows)
      ? tableData.data.rows
      : [];

    const matchedRow = rows.find((row) =>
      Object.values(row || {}).some((value) => {
        if (
          typeof value !== "number" &&
          typeof value !== "string"
        ) {
          return false;
        }

        const numericValue = Number(
          String(value).replace(/,/g, "").trim()
        );

        return (
          Number.isFinite(numericValue) &&
          Math.abs(
            numericValue - numericMeasuredValue
          ) < 0.0000001
        );
      })
    );

    return getSelectedLabvSampleKey(matchedRow);
  };

  const resolveSelectedLabvValue = (
    record,
    dbColumnName,
    fallbackDbColumnName = null
  ) => {
    if (!record) {
      return null;
    }

    if (
      dbColumnName === "SamRadius" ||
      dbColumnName === "Times/TimedAUL"
    ) {
      return record.keyid1 ?? null;
    }

    const normalizedDbColumnName = normalizeSearchKey(
      dbColumnName
    );

    const isMoistureField =
      normalizedDbColumnName.includes("moisture");

    const isGelBedField =
      normalizedDbColumnName.includes("gelbed") ||
      normalizedDbColumnName.includes("permeability");

    const parseNumericCandidate = (rawValue) => {
      if (
        rawValue === undefined ||
        rawValue === null
      ) {
        return null;
      }

      if (typeof rawValue === "number") {
        return Number.isNaN(rawValue)
          ? null
          : rawValue;
      }

      const matched = String(rawValue)
        .replace(/,/g, "")
        .match(/-?\d+(\.\d+)?/);

      if (!matched) {
        return null;
      }

      const parsed = Number(matched[0]);

      return Number.isNaN(parsed) ? null : parsed;
    };

    if (
      dbColumnName &&
      hasMeaningfulValue(record[dbColumnName])
    ) {
      return record[dbColumnName];
    }

    if (isGelBedField) {
      const gelBedValue = getRowValueByAliases(
        record,
        [
          "gelBedPermeability",
          "permeability",
          "Value",
          "value",
          "AVG",
          "avgvalue",
        ]
      );

      if (hasMeaningfulValue(gelBedValue)) {
        return (
          parseNumericCandidate(gelBedValue) ??
          gelBedValue
        );
      }

      const gelBedKeys = Object.keys(record).filter(
        (key) => {
          const normalizedKey = normalizeSearchKey(key);

          return (
            (normalizedKey.includes("gelbed") ||
              normalizedKey.includes("permeability")) &&
            hasMeaningfulValue(record[key])
          );
        }
      );

      if (gelBedKeys.length > 0) {
        return record[gelBedKeys[0]];
      }

      return null;
    }

    if (isMoistureField) {
      const moistureValue = getRowValueByAliases(
        record,
        [
          "moisture",
          "Value",
          "value",
          "AVG",
          "avgvalue",
        ]
      );

      if (hasMeaningfulValue(moistureValue)) {
        return moistureValue;
      }

      const moistureKeys = Object.keys(record).filter(
        (key) =>
          normalizeSearchKey(key).includes("moist")
      );

      if (moistureKeys.length > 0) {
        const numericKey = moistureKeys.find(
          (key) => !Number.isNaN(Number(record[key]))
        );

        return record[numericKey || moistureKeys[0]];
      }

      return null;
    }

    if (hasMeaningfulValue(record.avgvalue)) {
      return record.avgvalue;
    }

    if (
      record.target &&
      typeof record.target === "object" &&
      record.target.value !== undefined
    ) {
      return record.target.value;
    }

    const normalized = normalizeSearchKey(
      dbColumnName
    );

    const normalizedFallback = normalizeSearchKey(
      fallbackDbColumnName
    );

    const matchedKey = Object.keys(record).find(
      (key) =>
        normalizeSearchKey(key) === normalized
    );

    if (matchedKey) {
      return record[matchedKey];
    }

    if (normalizedFallback) {
      const fallbackKey = Object.keys(record).find(
        (key) =>
          normalizeSearchKey(key) ===
          normalizedFallback
      );

      if (fallbackKey) {
        return record[fallbackKey];
      }
    }

    const candidateKeys = Object.keys(record).filter(
      (key) => {
        const normalizedKey = normalizeSearchKey(key);

        const primaryMatch =
          normalized &&
          (normalizedKey.includes(normalized) ||
            normalized.includes(normalizedKey));

        const fallbackMatch =
          normalizedFallback &&
          (normalizedKey.includes(normalizedFallback) ||
            normalizedFallback.includes(normalizedKey));

        return (
          hasMeaningfulValue(record[key]) &&
          (primaryMatch || fallbackMatch)
        );
      }
    );

    if (candidateKeys.length > 0) {
      const numericKey = candidateKeys.find(
        (key) => !Number.isNaN(Number(record[key]))
      );

      return record[numericKey || candidateKeys[0]];
    }

    if (isMoistureField || isGelBedField) {
      return null;
    }

    const excludedKeys = new Set([
      "key",
      "requestid",
      "requestorname",
      "productcategory",
      "productdescription",
      "completeddt",
      "replicateid",
      "keyid1",
      "sampleid",
      "sampleId",
    ]);

    const fallbackKey = Object.keys(record).find(
      (key) => {
        if (excludedKeys.has(key)) {
          return false;
        }

        const value = record[key];

        return (
          (typeof value === "number" ||
            typeof value === "string") &&
          hasMeaningfulValue(value)
        );
      }
    );

    return fallbackKey ? record[fallbackKey] : null;
  };

  const getLabvRowSelectionKey = (
    record,
    rowIndex
  ) => {
    if (!record) {
      return undefined;
    }

    const suffix =
      rowIndex !== undefined && rowIndex !== null
        ? `-${rowIndex}`
        : "";

    if (
      record.key !== undefined &&
      record.key !== null
    ) {
      return String(record.key);
    }

    if (
      record.keyid1 !== undefined &&
      record.keyid1 !== null
    ) {
      return `keyid1-${record.keyid1}${suffix}`;
    }

    if (
      record.requestid !== undefined &&
      record.requestid !== null
    ) {
      return `requestid-${record.requestid}${suffix}`;
    }

    if (
      record.replicateid !== undefined &&
      record.replicateid !== null
    ) {
      return `replicateid-${record.replicateid}${suffix}`;
    }

if (
      record.sampleId !== undefined &&
      record.sampleId !== null
    ) {
      return `sampleId-${record.sampleId}${suffix}`;
    }

    if (
      record.sampleid !== undefined &&
      record.sampleid !== null
    ) {
      return `sampleId-${record.sampleid}${suffix}`;
    }

    return rowIndex !== undefined && rowIndex !== null
      ? `row-${rowIndex}`
      : undefined;
  };

  const fetchMaterialMetaData = useCallback(async () => {
    dispatch(fetchMaterialTypes());
    dispatch(fetchAutorTypes());
    dispatch(fetchMaterialColumns());
  }, [dispatch]);

  const finishCalculatorRequest = useCallback(
    (requestId, errorMessage = "") => {
      if (
        !calculatorMountedRef.current ||
        requestId !== calculatorRequestIdRef.current ||
        !calculatorRequestActiveRef.current
      ) {
        return;
      }

      calculatorRequestActiveRef.current = false;

      if (calculatorRunTimeoutRef.current !== null) {
        clearTimeout(calculatorRunTimeoutRef.current);
        calculatorRunTimeoutRef.current = null;
      }

      setIsCalculatorRunning(false);
      setCalculatorRunError(errorMessage);
    },
    []
  );

  useEffect(() => {
    calculatorMountedRef.current = true;

    return () => {
      calculatorMountedRef.current = false;
      calculatorRequestActiveRef.current = false;

      if (calculatorRunTimeoutRef.current !== null) {
        clearTimeout(calculatorRunTimeoutRef.current);
        calculatorRunTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    dispatch(updateSearchKey(""));

    if (searchType === "material") {
      fetchMaterialMetaData();
    }
  }, [
    dispatch,
    fetchMaterialMetaData,
    searchType,
  ]);

  useEffect(() => {
    const labVantageDetail =
      queryIdForSelectedPropertyRow?.data
        ?.labVantageDetail;

    if (labVantageDetail?.data?.length > 0) {
      const tabs = labVantageDetail.data;
      const defaultValues = {};

      tabs.forEach((item) => {
        defaultValues[item.dbColumnName] = null;
      });

      const activeTabStillPresent = tabs.find(
        (item) =>
          item.dbColumnName ===
            activeLabVantageTab?.dbColumnName &&
          (item.queryId || item.labVantageApi) ===
            (activeLabVantageTab?.queryId ||
              activeLabVantageTab?.labVantageApi)
      );

      const nextActiveTab =
        activeTabStillPresent || tabs[0];

      setLabVantageActiveTab(nextActiveTab);
      setSelectedApiTab(nextActiveTab);
      setLastLabvSelection(null);

      setLabVantageInputs((previous) => ({
        ...defaultValues,
        ...previous,
      }));

      setLabVantageTabValues((previous) => {
        const nextValues = { ...previous };

        tabs.forEach((item) => {
          const tabKey = getLabVantageTabKey(item);

          if (
            !Object.prototype.hasOwnProperty.call(
              nextValues,
              tabKey
            )
          ) {
            nextValues[tabKey] =
              defaultValues[item.dbColumnName];
          }
        });

        return nextValues;
      });
    }

    if (calculatorDetail?.data?.length) {
      const defaultCalculatorValues = {};

      calculatorDetail.data.forEach((item) => {
        defaultCalculatorValues[item.dbColumnName] =
          item.fieldDefaultValue;
      });

      const samDensityField =
        calculatorDetail.data.find(
          isSamDensityCalculatorField
        );

      const samDensityKey =
        samDensityField?.dbColumnName || "SAMDensity";

      const initializationKey = [
        props.urlId || "",
        props.advancedSearchMetaData?.key || "",
        calculatorDetail.calculatorName || "",
        samDensityKey,
      ].join("|");

      setAdvanceSearchFieldValues((previous) => {
        const defaultValuesWithCalculator = applyCalculatorDefaultValues(
          calculatorDetail.calculatorName,
          defaultCalculatorValues,
          calculatorDetail.data
        );

        // Start with previous (preserve all user-entered filters)
        // Then overlay only the calculator defaults
        const nextValues = {
          ...previous,
          ...defaultValuesWithCalculator,
        };

        const samDensityWasInitialized =
          samDensityInitializedFor.current ===
          initializationKey;

        if (samDensityWasInitialized) {
          const previousHasSamDensity =
            Object.prototype.hasOwnProperty.call(
              previous,
              samDensityKey
            );

          if (previousHasSamDensity) {
            nextValues[samDensityKey] =
              previous[samDensityKey];
          } else {
            delete nextValues[samDensityKey];
          }
        }

        return nextValues;
      });
    }

    if (
      calculatorDetail?.calculatorName &&
      labVantageDetail &&
      calculatorDetail.totalCount === 0 &&
      labVantageDetail.totalCount === 0
    ) {
      setAutoCallCalulator(true);
      dispatch(resetCalulatorApiResult());

      dispatch(
        fetchCalulatorApiResult({
          calcName: calculatorDetail.calculatorName,
        })
      );

      setSelectedRowKeys([-1]);
    }
  }, [
    dispatch,
    queryIdForSelectedPropertyRow,
    calculatorDetail,
    activeLabVantageTab,
    props.urlId,
    props.advancedSearchMetaData?.key,
  ]);

  useEffect(() => {
    const samDensityField =
      calculatorDetail?.data?.find(
        isSamDensityCalculatorField
      );

    const samPropertyData =
      props.moduleDefaultFieldDataSet?.sam
        ?.propertyData || [];

    const dryGelDensity = getCurrentSamPropertyValue(
      "solidsDryGelDensity"
    );

    if (isDevelopmentBuild) {
      console.debug(
        "[AdvanceSearch] SAM Density initialization verification",
        {
          calculatorName:
            calculatorDetail?.calculatorName,
          samDensityField,
          samPropertyData,
          dryGelDensity,
          sourceQueryId: DRY_GEL_DENSITY_QUERY_ID,
          sourceField: "solidsDryGelDensity",
        }
      );
    }

    if (!samDensityField) {
      return;
    }

    const samDensityKey =
      samDensityField.dbColumnName || "SAMDensity";

    if (!hasMeaningfulValue(dryGelDensity)) {
      return;
    }

    const initializationKey = [
      props.urlId || "",
      props.advancedSearchMetaData?.key || "",
      calculatorDetail.calculatorName || "",
      samDensityKey,
    ].join("|");

    if (
      samDensityInitializedFor.current ===
      initializationKey
    ) {
      return;
    }

    setAdvanceSearchFieldValues((previous) => ({
      ...previous,
      [samDensityKey]: dryGelDensity,
}));

    samDensityInitializedFor.current = initializationKey;
  }, [
    calculatorDetail,
    props.urlId,
    props.advancedSearchMetaData?.key,
    props.moduleDefaultFieldDataSet,
  ]);

  useEffect(() => {
    if (
      searchType !== "labvantage" ||
      !activeLabVantageTab
    ) {
      return;
    }

    const tabKey = getLabVantageTabKey(
      activeLabVantageTab
    );

    setLabVantageTabSnapshots((previous) => {
      const existing = previous[tabKey];

      if (existing?.tableData === tableData) {
        return previous;
      }

      return {
        ...previous,
        [tabKey]: {
          ...existing,
          tableData: cloneDeep(tableData),
        },
      };
    });
  }, [
    tableData,
    searchType,
    activeLabVantageTab,
  ]);

  const handleSearchTypeChangeEvent = (event) => {
    dispatch(resetMaterailSearchReucer({}));
    setSelectedColumns({});
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setLastLabvSelection(null);
    setSubmittedLabVantageSearchParams({});
    setAdvanceSearchFieldValues({});
    setLabVantageTabSnapshots({});
    setLabVantageTabValues({});
    setLabVantageTabErrors({});
    samDensityInitializedFor.current = null;
    manualMetadataRef.current = {};
    selectedInputMetadataRef.current = {};
    selectionRecordRef.current = null;
    setManualMetadataByTab({});

    dispatch(fetchAutorTypes());

    setFiberAvgError({
      code: "",
      response: "",
      type: "",
    });

    if (event.target.value === "material") {
      fetchMaterialMetaData();
    }

    setSearchType(event.target.value);
  };

  const handleRunCalcEvent = () => {
    if (
      isCalculatorRunning ||
      calculatorRequestActiveRef.current
    ) {
      return;
    }

    if (
      (searchType !== "labvantage" &&
        searchType !== "compression") ||
      props.modal.calulatorForm !== true
    ) {
      return;
    }

    if (calculatorDetail === undefined) {
      // Surface the silent no-op so it's diagnosable instead of the button appearing to do nothing.
      message.warning(
        "Calculator details are not loaded yet. Please reopen this calculator and try again."
      );

      return;
    }

    const tempLabVantageInputs = cloneDeep(
      labVantageInputs
    );

    const tempAdvanceSearchFieldValues =
      cloneDeep(advanceSearchFieldValues);

    const calcParams = {
      calcName: calculatorDetail.calculatorName,
      params: tempAdvanceSearchFieldValues,
    };

    Object.assign(
      calcParams.params,
      tempLabVantageInputs
    );

    const samDensityField =
      calculatorDetail.data?.find(
        isSamDensityCalculatorField
      );

    const samDensityKey =
      samDensityField?.dbColumnName || "SAMDensity";

    if (
      Object.prototype.hasOwnProperty.call(
        tempAdvanceSearchFieldValues,
        samDensityKey
      )
    ) {
      calcParams.params[samDensityKey] =
        tempAdvanceSearchFieldValues[samDensityKey];
    }

    if (
      calcParams.calcName === "GelProp_C" ||
      calcParams.calcName === "CalcDiff"
    ) {
      const selectedRecord =
        selectedRows?.[0] ||
        lastLabvSelection?.record ||
        null;

      const currentTimesInput =
        calcParams.params["Times/TimedAUL"];


      const selectedSampleKey =
        getSelectedLabvSampleKey(selectedRecord) ||
        getLabvSampleKeyForMeasuredValue(
          currentTimesInput
        );

      if (selectedSampleKey) {
        calcParams.params["Times/TimedAUL"] =
          selectedSampleKey;
      } else {
        calcParams.params = sanitizeRequestParams(
          calcParams.params
        );
      }
    }

    if (
      searchType === "compression" &&
      calculatorDetail.data?.length > 0
    ) {
      calcParams.params.equationType =
        calculatorDetail.data[0].equationType;

      calcParams.params.testState =
        props.advancedSearchMetaData.key
          .toLowerCase()
          .includes("dry")
          ? "dry"
          : "wet";

      calcParams.materialId = props.urlId;
    }

    const requestId = calculatorRequestIdRef.current + 1;

    calculatorRequestIdRef.current = requestId;
    // Snapshot the exact submitted request, never later edited UI values.
    calculatorRequestProvenanceRef.current = {
      propertyKey: props.advancedSearchMetaData?.key,
      calculatorName: calcParams.calcName,
      inputParameters: cloneDeep(calcParams.params),
      inputMetaData: snapshotCalculatorInputMetadata(selectedInputMetadataRef.current, manualMetadataRef.current),
      calculatorVersion: calculatorDetail.calculatorVersion,
    };
    calculatorRequestActiveRef.current = true;

    setCalculatorRunError("");
    setIsCalculatorRunning(true);

    try {
      dispatch(resetCalulatorApiResult());

      const request = dispatch(
        fetchCalulatorApiResult(calcParams)
      );

      calculatorRunTimeoutRef.current = setTimeout(() => {
        finishCalculatorRequest(
          requestId,
          "The calculator request timed out. Please try again."
        );
      }, 60000);

      if (request && typeof request.then === "function") {
        request
          .then((response) => {
            const responseType = response?.data?.responseType;

            if (
              response?.error ||
              responseType === "Failed" ||
              responseType === "failed"
            ) {
              finishCalculatorRequest(
                requestId,
                response?.data?.message ||
                  response?.message ||
                  "The calculator request failed."
              );
            }
          })
          .catch((error) => {
            finishCalculatorRequest(
              requestId,
              error?.message ||
                "The calculator request failed."
            );
          })
          .finally(() => {
            finishCalculatorRequest(requestId);
          });
      } else {
        finishCalculatorRequest(requestId);
      }
    } catch (error) {
      finishCalculatorRequest(
        requestId,
        error?.message || "The calculator request failed."
      );
    }
  };

  const getExistingPropertySource = (fieldKey) => {
    const module = props.advancedSearchMetaData?.module;
    const storageModule = ["mechanical", "absorbent"].includes(module) ? "material" : module;
    const rows = props.moduleDefaultFieldDataSet?.[storageModule]?.propertyData || [];
    const find = (items) => {
      for (const row of Array.isArray(items) ? items : []) {
        if (row && Object.prototype.hasOwnProperty.call(row, fieldKey)) return row[fieldKey];
        for (const field of Object.values(row || {})) {
          if (field?.valueTypeArray) {
            const child = find(field.value);
            if (child) return child;
          }
        }
      }
      return undefined;
    };
    const field = find(rows) || props.advancedSearchMetaData?.item?.[fieldKey];
    return normalizeSamSource(field?.dataSource);
  };

  const getActiveManualMetadata = () => {
    const tabKey = getLabVantageTabKey(activeLabVantageTab);
    const fieldKey = activeLabVantageTab?.dbColumnName || props.advancedSearchMetaData?.key;
    return {
      ...cloneDeep(defaultMetaDataFieldsList?.data?.default?.metaData || {}),
      ...cloneDeep(getExistingPropertySource(fieldKey).metaData || {}),
      ...cloneDeep(manualMetadataByTab[tabKey] || {}),
      ...cloneDeep(manualMetadataRef.current[tabKey] || {}),
      ...(otherSourceType === "others" ? { source: defaultMetaDataFieldsList?.data?.default?.metaData?.source || "manual" } : {}),
    };
  };

  const updateManualMetadata = (event, key) => {
    if (["__proto__", "constructor", "prototype"].includes(key)) return;
    const tabKey = getLabVantageTabKey(activeLabVantageTab);
    const metadata = trackSamMetadataClear({ ...getActiveManualMetadata(), [key]: event.target.value }, key, event.target.value);
    const next = { ...manualMetadataRef.current, [tabKey]: metadata };
    manualMetadataRef.current = next;
    setManualMetadataByTab(next);
  };

  const buildSelectionDataSource = (selectedRow, fieldKey, options = {}) => {
    const existing = options.includeExisting === false ? {} : getExistingPropertySource(fieldKey);
    let source = mergeMetadataSource(existing, tableData.data?.dataSource);
    source.propertyName = fieldKey;
    if (otherSourceType === "others") {
      source.type = "physical";
      // Do not merge a previous LabVantage row over explicitly entered text.
      source.metaData = {
        ...source.metaData,
        ...cloneDeep(getActiveManualMetadata()),
        source: defaultMetaDataFieldsList?.data?.default?.metaData?.source || "manual",
      };
      if (userInfo?.sAMAccountName) source.metaData.author = userInfo.sAMAccountName;
      return source;
    }
    const selectedMetadata = samMetadataFromRow(selectedRow);
    // Restore the original SAM mapping for legacy LabVantage rows. Prefer a
    // real request date; older responses only provide the completion date.
    if (samMissing(selectedMetadata.requestDate)) {
      const completion = Object.entries(samObject(selectedRow)).find(([key, value]) =>
        ["completeddt", "completeddate", "completiondate"].includes(samNormalizeKey(key)) && !samMissing(value));
      if (completion) selectedMetadata.requestDate = cloneDeep(completion[1]);
    }
    source = mergeMetadataSource(source, { metaData: selectedMetadata });
    source = mergeMetadataSource(source, { metaData: {
      source: "labvantage",
      author: source.metaData.author || userInfo?.sAMAccountName,
      sampleId: getSelectedLabvSampleId(selectedRow) || getSelectedLabvSampleKey(selectedRow) || source.metaData.sampleId,
      description: getSelectedLabvDescription(selectedRow) || source.metaData.description,
    } });
    return source;
  };

  const handleUseSectionEvent = () => {
    const tempKey = selectedRowKeys?.[0];

    if (searchType === "material") {
      dispatch(
        getMaterialPropertyDetailById({ id: tempKey })
      ).then((response) => {
        if (
          response.data.responseType === "success"
        ) {
          props.closeAdvancedSearchModal(false);
        }
      });

      return;
    }

    if (searchType === "sam") {
      dispatch(
        getSamPropertyDetailById({ id: tempKey })
      ).then((response) => {
        if (
          response.data.responseType === "success"
        ) {
          props.importSearchResultSet({
            moduleType: searchType,
            dataSet: response.data.responseData,
          });

          props.closeAdvancedSearchModal(false);
        }
      });

      return;
    }

    if (searchType === "fiber") {
      dispatch(
        getFiberPropertyDetailById({ id: tempKey })
      ).then((response) => {
        if (
          response.data.responseType === "success"
        ) {
          props.importSearchResultSet({
            moduleType: "fiberDefault",
            dataSet: response.data.responseData,
          });

          props.closeAdvancedSearchModal(false);
        }
      });

      return;
    }

    if (searchType !== "labvantage") {
      return;
    }

    let selectedRow =
      selectedRows?.length > 0
        ? selectedRows[0]
        : null;

    if (!selectedRow && lastLabvSelection?.record) {
      selectedRow = lastLabvSelection.record;
    }

    const rows = Array.isArray(tableData.data?.rows)
      ? tableData.data.rows
      : [];

    if (!selectedRow) {
      selectedRow = rows.find(
        (row, rowIndex) =>
          String(
            getLabvRowSelectionKey(row, rowIndex)
          ) === String(tempKey) ||
          String(getLabvRowSelectionKey(row)) ===
            String(tempKey) ||
          String(row.key) === String(tempKey)
      );
    }

    if (
      !selectedRow &&
      Number.isFinite(Number(tempKey))
    ) {
      selectedRow = rows[Number(tempKey)];
    }

    const selectedDbColumnName =
      getEffectiveLabvFieldKey();

    const selectedInputValuePrimary =
      selectedDbColumnName
        ? labVantageInputs[selectedDbColumnName]
        : null;

    const selectedInputValueFallback =
      activeLabVantageTab?.dbColumnName
        ? labVantageInputs[
            activeLabVantageTab.dbColumnName
          ]
        : null;

    const selectedInputValue =
      selectedInputValuePrimary !== undefined &&
      selectedInputValuePrimary !== null &&
      selectedInputValuePrimary !== ""
        ? selectedInputValuePrimary
        : selectedInputValueFallback;

    const targetFieldKey =
      props?.advancedSearchMetaData?.key || "";

    const targetFieldLabel =
      props?.advancedSearchMetaData?.fieldLabel || "";

    const normalizedTargetKey =
      normalizeSearchKey(targetFieldKey);

    const normalizedTargetLabel =
      normalizeSearchKey(targetFieldLabel);

    const selectedDbColumnNameNormalized =
      normalizeSearchKey(selectedDbColumnName);

    const activeTabDbColumnNameNormalized =
      normalizeSearchKey(
        activeLabVantageTab?.dbColumnName
      );

    const activeQueryIdNormalized = String(
      activeLabVantageTab?.queryId ||
        activeLabVantageTab?.labVantageApi ||
        submittedLabVantageSearchParams?.queryId ||
        ""
    )
      .replace(/\s+/g, "")
      .toUpperCase();

    const isDryGelDensityTarget =
      normalizeSearchKey(
        activeQueryIdNormalized
      ).includes(
        normalizeSearchKey(
          DRY_GEL_DENSITY_QUERY_ID
        )
      ) ||
      normalizedTargetKey.includes(
        "drygeldensity"
      ) ||
      normalizedTargetLabel.includes(
        "drygeldensity"
      ) ||
      isSamDensityKey(targetFieldKey) ||
      isSamDensityKey(selectedDbColumnName);

    const is03PsiGbpTarget =
      normalizedTargetKey === "03psigbp" ||
      normalizedTargetLabel.includes("03psigbp") ||
      selectedDbColumnNameNormalized === "03psigbp" ||
      activeTabDbColumnNameNormalized === "03psigbp" ||
      activeQueryIdNormalized.startsWith("STM-00220");

    const requiresSelectedRowValue =
      isDryGelDensityTarget || is03PsiGbpTarget;

    if (requiresSelectedRowValue) {
      const rawValue = getRowValueByAliases(selectedRow, [
        "Value",
        "value",
        "paramValue",
        "avgvalue",
        "AVG",
      ]);

      const numericValue = parseFloat(rawValue);

      if (
        rawValue === undefined ||
        rawValue === null ||
        String(rawValue).trim() === "" ||
        Number.isNaN(numericValue)
      ) {
        message.warning(
          "Selected row is missing a numeric value. Please select a valid row"
        );

        return;
      }

      const effectiveTargetFieldKey =
        isDryGelDensityTarget
          ? "solidsDryGelDensity"
          : targetFieldKey || "0.3psiGBP";

      const tempDataSource = buildSelectionDataSource(selectedRow, effectiveTargetFieldKey);

      props.importSearchResultSet({
        moduleType: "labvantage",
        dataSet: {
          ...(selectedRow || {}),
          [effectiveTargetFieldKey]: numericValue,
          __targetFieldKey: effectiveTargetFieldKey,
          __selectedDbColumnName:
            effectiveTargetFieldKey,
          __selectedValue: numericValue,
          __selectedInputValue: numericValue,
          __forceFieldKey: effectiveTargetFieldKey,
          __forceFieldLabel: targetFieldLabel,
          __forceValue: numericValue,
        },
        dataSource: tempDataSource,
        dataSourceComplete: true,
      });

      props.closeAdvancedSearchModal(false);
      return;
    }

    const isMoistureTarget =
      normalizedTargetKey === "moisture" ||
      normalizedTargetLabel.includes("moisture") ||
      selectedDbColumnNameNormalized.includes(
        "moist"
      ) ||
      activeTabDbColumnNameNormalized.includes(
        "moist"
      ) ||
      activeQueryIdNormalized.startsWith(
        "STM-00378"
      );

    const isGelBedTarget =
      normalizedTargetKey ===
        "gelbedpermeability" ||
      normalizedTargetLabel.includes(
        "gelbedpermeability"
      ) ||
      selectedDbColumnNameNormalized.includes(
        "gelbed"
      ) ||
      selectedDbColumnNameNormalized.includes(
        "permeability"
      ) ||
      activeTabDbColumnNameNormalized.includes(
        "gelbed"
      ) ||
      activeTabDbColumnNameNormalized.includes(
        "permeability"
      ) ||
      activeQueryIdNormalized.startsWith(
        "STM-00229"
      );

    const resolvedFromRowPrimary = selectedRow
      ? resolveSelectedLabvValue(
          selectedRow,
          selectedDbColumnName,
          activeLabVantageTab?.dbColumnName
        )
      : null;

    const resolvedFromRowFallback = selectedRow
      ? resolveSelectedLabvValue(
          selectedRow,
          activeLabVantageTab?.dbColumnName,
          selectedDbColumnName
        )
      : null;

    const directSelectedValue = [
      resolvedFromRowPrimary,
      resolvedFromRowFallback,
      selectedInputValue,
      lastLabvSelection?.value,
    ].find(hasMeaningfulValue);

    if (isGelBedTarget) {
      const rawValue =
        getRowValueByAliases(selectedRow, [
          "gelBedPermeability",
          "permeability",
          "Value",
          "value",
          "AVG",
          "avgvalue",
        ]) ?? directSelectedValue;

      const numericValue = parseFloat(rawValue);

      if (
        rawValue === undefined ||
        rawValue === null ||
        String(rawValue).trim() === "" ||
        Number.isNaN(numericValue)
      ) {
        message.warning(
          "Selected row is missing a numeric value. Please select a valid row"
        );

        return;
      }

      // Calculator form: populate the active tab's Value column in
      // "Inputs from other sources" before also writing the value through
      // to the outer SAM property below so the underlying UI updates.
      if (props.modal.calulatorForm === true) {
        const activeTabKey = getLabVantageTabKey(
          activeLabVantageTab
        );

        setLabVantageTabErrors((previous) => ({
          ...previous,
          [activeTabKey]: false,
        }));

        setLabVantageInputs((previous) => ({
          ...previous,
          [activeLabVantageTab?.dbColumnName]:
            numericValue,
        }));

        updateStoredLabVantageTabValue(
          activeLabVantageTab,
          numericValue
        );

        setLastLabvSelection({
          record: selectedRow,
          rowKey: tempKey,
          fieldKey: activeLabVantageTab?.dbColumnName,
          value: numericValue,
        });
      }

      props.importSearchResultSet({
        moduleType: "labvantage",
        dataSet: {
          ...(selectedRow || {}),
          gelBedPermeability: numericValue,
          avgvalue: numericValue,
          __targetFieldKey:
            "gelBedPermeability",
          __selectedDbColumnName:
            "gelBedPermeability",
          __selectedValue: numericValue,
          __selectedInputValue: numericValue,
          __forceFieldKey:
            "gelBedPermeability",
          __forceValue: numericValue,
        },
        dataSource: buildSelectionDataSource(selectedRow, "gelBedPermeability"),
        dataSourceComplete: true,
      });

      props.closeAdvancedSearchModal(false);
      return;
    }

    if (hasMeaningfulValue(directSelectedValue)) {
      const effectiveTargetFieldKey =
        isMoistureTarget
          ? "moisture"
          : targetFieldKey;

      const effectiveSelectedDbColumnName =
        isMoistureTarget
          ? "moisture"
          : selectedDbColumnName;

      props.importSearchResultSet({
        moduleType: "labvantage",
        dataSet: {
          ...(selectedRow || {}),
          [effectiveSelectedDbColumnName]:
            directSelectedValue,
          avgvalue: directSelectedValue,
          __targetFieldKey:
            effectiveTargetFieldKey,
          __selectedDbColumnName:
            effectiveSelectedDbColumnName,
          __selectedValue: directSelectedValue,
          __selectedInputValue: selectedInputValue,
          __forceFieldKey:
            effectiveTargetFieldKey,
          __forceFieldLabel: targetFieldLabel,
          __forceValue: directSelectedValue,
        },
        dataSource: buildSelectionDataSource(selectedRow, effectiveTargetFieldKey),
        dataSourceComplete: true,
      });

      props.closeAdvancedSearchModal(false);
      return;
    }
  };

  const handleUseSectionEventForCalcResults = () => {
    if (searchType !== "labvantage" && searchType !== "compression") return;
    const result = cloneDeep(calulatorApiResultData);
    if (!result?.data || !Array.isArray(result.data.outputData) || !result.data.outputData.length) {
      message.warning("Run the calculator and select a valid result before importing.");
      return;
    }
    const output = result.data.outputData;
    if (output.some((item) => !item || typeof item.dbColumnName !== "string" || !item.dbColumnName.trim()) ||
        new Set(output.map((item) => item.dbColumnName)).size !== output.length) {
      message.warning("Calculator outputs must have unique property names. No result was imported.");
      return;
    }
    const commonSource = normalizeSamSource(result.data.dataSource);
    const responseProvenance = samMetadataFromRow(result.data);
    const submitted = calculatorRequestProvenanceRef.current;
    const requestMatches = submitted?.propertyKey === props.advancedSearchMetaData?.key &&
      submitted?.calculatorName === calculatorDetail?.calculatorName;
    [...samMetadataKeys, ...samCalculatorMetadataKeys].forEach((key) => {
      if (samMissing(commonSource.metaData[key]) && !samMissing(responseProvenance[key]))
        commonSource.metaData[key] = cloneDeep(responseProvenance[key]);
      if (samCalculatorMetadataKeys.includes(key) && samMissing(commonSource.metaData[key]) && requestMatches && !samMissing(submitted?.[key]))
        commonSource.metaData[key] = cloneDeep(submitted[key]);
    });
    // Use the inputs that produced the numeric result, not edits made afterward.
    const inputMetadata = requestMatches && submitted.inputMetaData
      ? cloneDeep(submitted.inputMetaData)
      : snapshotCalculatorInputMetadata(selectedInputMetadataRef.current, manualMetadataRef.current);
    const common = commonSamMetadata(Object.values(inputMetadata).map((metaData) => ({ metaData })));
    const conflicting = samMetadataKeys.filter((key) => {
      if (key === "source") return false;
      const actual = Object.values(inputMetadata).filter((metadata) => !samMissing(metadata[key]));
      return actual.length > 1 && !actual.every((metadata) => JSON.stringify(metadata[key]) === JSON.stringify(actual[0][key]));
    });
    const completeSource = (fieldKey, perOutput = {}) => {
      const explicit = mergeMetadataSource(commonSource, perOutput);
      const source = mergeMetadataSource(getExistingPropertySource(fieldKey), explicit);
      source.propertyName = fieldKey;
      // Fresh input metadata wins over the previous saved result, but a
      // service-supplied output value wins over inherited input metadata.
      Object.entries(common).forEach(([key, value]) => {
        if (key !== "source" && samMissing(explicit.metaData[key])) source.metaData[key] = cloneDeep(value);
      });
      conflicting.forEach((key) => {
        // Do not falsely attribute a mixed-sample calculation to its OLD
        // sample/method/date. Every actual input remains in inputMetaData.
        if (samMissing(explicit.metaData[key])) {
          source.metaData[key] = null;
          // Also block legacy aliases/root metadata from resurrecting the
          // previous sample attribution during normalization after reload.
          trackSamMetadataClear(source.metaData, key, null);
        }
      });
      samMetadataKeys.forEach((key) => {
        const supplied = Object.values(inputMetadata);
        const explicitlyCleared = supplied.some((metadata) => samMetadataWasCleared(metadata, key));
        if (key !== "source" && explicitlyCleared && samMissing(common[key]) && samMissing(explicit.metaData[key])) {
          source.metaData[key] = "";
          trackSamMetadataClear(source.metaData, key, "");
        }
      });
      samCalculatorMetadataKeys.forEach((key) => {
        if (!samMissing(explicit.metaData[key])) source.metaData[key] = cloneDeep(explicit.metaData[key]);
        else if (requestMatches) delete source.metaData[key];
      });
      samMetadataKeys.forEach((key) => {
        if (key !== "source" && samMetadataWasCleared(explicit.metaData, key)) {
          source.metaData[key] = "";
          trackSamMetadataClear(source.metaData, key, "");
        }
      });
      source.metaData.source = explicit.metaData.source || "Calculator";
      if (userInfo?.sAMAccountName && samMissing(source.metaData.author) && !samMetadataWasCleared(source.metaData, "author")) source.metaData.author = userInfo.sAMAccountName;
      // Replace the input snapshot, not a deep merge with a previous run.
      if (Object.keys(inputMetadata).length) source.metaData.inputMetaData = cloneDeep(inputMetadata);
      else if (requestMatches) delete source.metaData.inputMetaData;
      // A new explicit value supersedes a prior manual clear for that key.
      if (Array.isArray(source.metaData._manualClears)) {
        source.metaData._manualClears = source.metaData._manualClears.filter((key) =>
          samMetadataWasCleared(explicit.metaData, key) ||
          (samMissing(explicit.metaData[key]) && samMissing(common[key])));
        if (!source.metaData._manualClears.length) delete source.metaData._manualClears;
      }
      return source;
    };
    const sourcesByField = {};
    result.data.outputData = output.map((item) => {
      const source = completeSource(item.dbColumnName, samSourceFromOutput(item));
      sourcesByField[item.dbColumnName] = cloneDeep(source);
      // Support parents that read item.dataSource as well as those that read
      // dataSourcesByField. Both carry exactly this property's provenance.
      return { ...item, dataSource: cloneDeep(source) };
    });
    result.data.dataSourcesByField = sourcesByField;
    result.data.dataSource = output.length === 1
      ? cloneDeep(sourcesByField[output[0].dbColumnName]) : completeSource(undefined);
    props.importSearchResultSet({ moduleType: "labvantageCalcResults", dataSet: result, dataSourceComplete: true });
  };

  const handleUseAvgFiberEvent = () => {
    if (
      searchType === "fiber" &&
      fiberAvgError.type === "success" &&
      fiberAvgError.response !== "" &&
      fiberAvgError.response.rows.length > 0
    ) {
      props.importSearchResultSet({
        moduleType: searchType,
        dataSet: fiberAvgError.response.rows[0],
        dataSource:
          fiberAvgError.response.dataSource,
      });

      props.closeAdvancedSearchModal(false);
    }
  };

  const captureSelectedInputMetadata = (tab, record) => {
    if (!tab?.dbColumnName) return;
    const source = buildSelectionDataSource(record, tab.dbColumnName, { includeExisting: false });
    selectedInputMetadataRef.current[getLabVantageTabKey(tab)] = cloneDeep(source.metaData);
  };

  const updateStoredLabVantageTabValue = (
    tab,
    value
  ) => {
    const tabKey = getLabVantageTabKey(tab);
    if (samMissing(value)) {
      delete selectedInputMetadataRef.current[tabKey];
    } else if (otherSourceType === "others" || selectionRecordRef.current?.tabKey === tabKey) {
      captureSelectedInputMetadata(tab, selectionRecordRef.current?.record);
    }
    setLabVantageTabValues((previous) => ({
      ...previous,
      [getLabVantageTabKey(tab)]: value,
    }));
  };

  const onSelectChange = (
    record,
    selected
  ) => {
    if (selected) {
      onSelectRowEvent(record);
    }

    dispatch(resetCalulatorApiResultToInintal());
  };

  const onTableSelectionChange = (
    newSelectedRowKeys,
    newSelectedRows
  ) => {
    if (searchType === "fiber") {
      if ((newSelectedRowKeys || []).length > 3) {
        message.warning({
          content:
            "Maximum 3 fibers can be selected for average calculation",
          key: "fiberMaxSelection",
        });

        return;
      }

      const selectionChanged =
        JSON.stringify(
          [...(newSelectedRowKeys || [])].sort()
        ) !==
        JSON.stringify([...selectedRowKeys].sort());

      if (selectionChanged) {
        setFiberAvgError({
          code: "",
          response: "",
          type: "",
        });
      }
    }

    setSelectedRowKeys(newSelectedRowKeys || []);

    if (searchType === "labvantage") {
      return;
    }

    if (searchType !== "compression") {
      return;
    }

    setSelectedRows(newSelectedRows || []);

    if (newSelectedRows?.length > 0) {
      captureSelectedInputMetadata(activeLabVantageTab, newSelectedRows[0]);
      setLabVantageInputs((previous) => ({
        ...previous,
        sampleId: newSelectedRows[0].sampleId,
      }));
    }
  };

  const rowSelectionProps = {
    selectedRowKeys,
    type: "radio",
    onSelect: onSelectChange,
    onChange: onTableSelectionChange,
    preserveSelectedRowKeys: true,
    hideSelectAll: true,
  };

  const handleManualInput = (
    event,
    _currentManualInput,
    itemKey
  ) => {
    const nextValues = cloneDeep(
      labVantageInputs
    );

    const activeTab = activeLabVantageTab;

    if (!activeTab?.dbColumnName) {
      return;
    }

    if (
      nextValues[activeTab.dbColumnName] === null ||
      nextValues[activeTab.dbColumnName] ===
        undefined
    ) {
      nextValues[activeTab.dbColumnName] = {};
    }

    nextValues[activeTab.dbColumnName][itemKey] =
      event.target.value;

    setLabVantageInputs(nextValues);

    updateStoredLabVantageTabValue(
      activeTab,
      nextValues[activeTab.dbColumnName]
    );

    setSelectedRowKeys([-1]);
  };

  const onSelectRowEvent = (record, rowIndex) => {
    selectionRecordRef.current = { tabKey: getLabVantageTabKey(activeLabVantageTab), record };
    // Invalid replacements must not leave the previous row's metadata behind.
    delete selectedInputMetadataRef.current[getLabVantageTabKey(activeLabVantageTab)];
    if (
      searchType !== "fiber" &&
      searchType !== "labvantage" &&
      searchType !== "compression"
    ) {
      setSelectedRowKeys([record.key]);
    } else if (
      searchType === "labvantage" &&
      otherSourceType !== "others"
    ) {
      const rowSelectionKey =
        getLabvRowSelectionKey(record, rowIndex);

      if (rowSelectionKey !== undefined) {
        setSelectedRowKeys([
          String(rowSelectionKey),
        ]);
      }

      setSelectedRows([record]);

      const activeTabKey = getLabVantageTabKey(
        activeLabVantageTab
      );

      if (props.modal.calulatorForm === true) {
        // Sample-based calculators (GelProp_C, CalcDiff) need sample ID
        if (isSampleBasedCalculator) {
          const sampleId = getSelectedLabvSampleId(record);

          if (!sampleId) {
            setLabVantageTabErrors((previous) => ({
              ...previous,
              [activeTabKey]: true,
            }));

            message.warning(
              "Select a valid row with a sample ID."
            );

            setLastLabvSelection({
              record,
              rowKey: rowSelectionKey,
              fieldKey:
                activeLabVantageTab?.dbColumnName,
              value: null,
            });

            dispatch(
              resetCalulatorApiResultToInintal()
            );

            return;
          }

          setLabVantageTabErrors((previous) => ({
            ...previous,
            [activeTabKey]: false,
          }));

          setLabVantageInputs((previous) => ({
            ...previous,
            [activeLabVantageTab?.dbColumnName]:
              sampleId,
          }));

          updateStoredLabVantageTabValue(
            activeLabVantageTab,
            sampleId
          );

          setLastLabvSelection({
            record,
            rowKey: rowSelectionKey,
            fieldKey:
              activeLabVantageTab?.dbColumnName,
            value: sampleId,
          });

          dispatch(
            resetCalulatorApiResultToInintal()
          );

          return;
        }

        // Hooke's Law / Gel Osmotic Activity / Load Coefficients: numeric-only
        // Value, applied immediately to the active tab - no "Use Selection" button.
        if (requiresNumericTabValidation) {
          const rawValue = getRowValueByAliases(record, [
            "Value",
            "value",
            "paramValue",
            "avgvalue",
            "AVG",
          ]);

          const numericValue = parseFloat(rawValue);

          const isValid =
            rawValue !== undefined &&
            rawValue !== null &&
            String(rawValue).trim() !== "" &&
            !Number.isNaN(numericValue);

          if (!isValid) {
            setLabVantageTabErrors((previous) => ({
              ...previous,
              [activeTabKey]: true,
            }));

            message.warning(
              "Select a valid row only which has a numerical value."
            );

            setLastLabvSelection({
              record,
              rowKey: rowSelectionKey,
              fieldKey:
                activeLabVantageTab?.dbColumnName,
              value: null,
            });

            dispatch(
              resetCalulatorApiResultToInintal()
            );

            return;
          }

          setLabVantageTabErrors((previous) => ({
            ...previous,
            [activeTabKey]: false,
          }));

          setLabVantageInputs((previous) => ({
            ...previous,
            [activeLabVantageTab?.dbColumnName]:
              numericValue,
          }));

          updateStoredLabVantageTabValue(
            activeLabVantageTab,
            numericValue
          );

          setLastLabvSelection({
            record,
            rowKey: rowSelectionKey,
            fieldKey: activeLabVantageTab?.dbColumnName,
            value: numericValue,
          });

          dispatch(
            resetCalulatorApiResultToInintal()
          );

          return;
        }

        // Hooke's Law / Gel Osmotic Activity: fall through to the shared
        // multi-field population logic below (populates the per-AUL value
        // table immediately, no "Use Selection" button).
        if (!isHookesLawCalculator) {
          // Other calculators: Get numeric value from selected row
          const rawValue = getRowValueByAliases(record, [
            "Value",
            "value",
            "paramValue",
            "avgvalue",
            "AVG",
          ]);

          const numericValue = parseFloat(rawValue);

          // Validate that value is numeric
          const isValid =
            rawValue !== undefined &&
            rawValue !== null &&
            String(rawValue).trim() !== "" &&
            !Number.isNaN(numericValue);

          if (!isValid) {
            // Set tab to error state (red) when value is not numeric
            setLabVantageTabErrors((previous) => ({
              ...previous,
              [activeTabKey]: true,
            }));

            message.warning(
              "Select a valid row only which has a numerical value."
            );

            setLastLabvSelection({
              record,
              rowKey: rowSelectionKey,
              fieldKey:
                activeLabVantageTab?.dbColumnName,
              value: null,
            });

            dispatch(
              resetCalulatorApiResultToInintal()
            );

            return;
          }

          // Valid numeric row: populate the Value column immediately so it's
          // visible in "Inputs from other sources", "Use Selection" still
          // finalizes the value into the actual SAM property.
          setLabVantageTabErrors((previous) => ({
            ...previous,
            [activeTabKey]: false,
          }));

          setLabVantageInputs((previous) => ({
            ...previous,
            [activeLabVantageTab?.dbColumnName]:
              numericValue,
          }));

          updateStoredLabVantageTabValue(
            activeLabVantageTab,
            numericValue
          );

          setLastLabvSelection({
            record,
            rowKey: rowSelectionKey,
            fieldKey: activeLabVantageTab?.dbColumnName,
            value: numericValue,
          });

          dispatch(
            resetCalulatorApiResultToInintal()
          );

          return;
        }
      }

      const nextValues = cloneDeep(
        labVantageInputs
      );

      const effectiveFieldKey =
        getEffectiveLabvFieldKey();

      const resolvedValue =
        resolveSelectedLabvValue(
          record,
          effectiveFieldKey,
          activeLabVantageTab?.dbColumnName
        );

      const cleanResolvedValue =
        hasMeaningfulValue(resolvedValue)
          ? resolvedValue
          : "";

      if (
        effectiveFieldKey &&
        !isSamDensityKey(effectiveFieldKey)
      ) {
        nextValues[effectiveFieldKey] =
          cleanResolvedValue;
      }

      if (
        activeLabVantageTab?.dbColumnName &&
        !isSamDensityKey(
          activeLabVantageTab.dbColumnName
        )
      ) {
        nextValues[
          activeLabVantageTab.dbColumnName
        ] = cleanResolvedValue;
      }

      const sampleKey =
        getSelectedLabvSampleKey(record);

      if (sampleKey) {
        nextValues["Times/TimedAUL"] =
          sampleKey;
      }

      setLabVantageInputs(nextValues);

      if (
        activeLabVantageTab?.dbColumnName &&
        !isSamDensityKey(
          activeLabVantageTab.dbColumnName
        )
      ) {
        updateStoredLabVantageTabValue(
          activeLabVantageTab,
          cleanResolvedValue
);
      }

      setLastLabvSelection({
        record,
        rowKey: rowSelectionKey,
        fieldKey: effectiveFieldKey,
        value: cleanResolvedValue,
      });
    } else if (
      searchType === "labvantage" &&
      otherSourceType === "others"
    ) {
      const rowSelectionKey =
        getLabvRowSelectionKey(record, rowIndex);

      if (rowSelectionKey !== undefined) {
        setSelectedRowKeys([
          String(rowSelectionKey),
        ]);
      }

      setSelectedRows([record]);

      const activeTabColumnName =
        activeLabVantageTab?.dbColumnName;

      const resolvedValue =
        resolveSelectedLabvValue(
          record,
          activeTabColumnName
        );

      const cleanResolvedValue =
        hasMeaningfulValue(resolvedValue)
          ? resolvedValue
          : "";

      if (
        activeTabColumnName &&
        !isSamDensityKey(activeTabColumnName)
      ) {
        setLabVantageInputs((previous) => ({
          ...previous,
          [activeTabColumnName]: cleanResolvedValue,
        }));

        updateStoredLabVantageTabValue(
          activeLabVantageTab,
          cleanResolvedValue
        );
      }

      setLastLabvSelection({
        record,
        rowKey: rowSelectionKey,
        fieldKey: activeTabColumnName,
        value: cleanResolvedValue,
      });
    } else if (searchType === "compression") {
      captureSelectedInputMetadata(activeLabVantageTab, record);
      setSelectedRows([record]);

      setLabVantageInputs((previous) => ({
        ...previous,
        sampleId: record.sampleId,
      }));
    } else if (searchType === "fiber") {
      const keyList = cloneDeep(selectedRowKeys);
      const rowList = cloneDeep(selectedRows);
      const index = keyList.indexOf(record.key);

      if (index === -1) {
        if (keyList.length >= 3) {
          message.warning({
            content:
              "Maximum 3 fibers can be selected for average calculation",
            key: "fiberMaxSelection",
          });

          return;
        }

        keyList.push(record.key);
        rowList.push(record);
      } else {
        keyList.splice(index, 1);
        rowList.splice(index, 1);
      }

      setFiberAvgError({
        code: "",
        response: "",
        type: "",
      });
      setSelectedRowKeys(keyList);
      setSelectedRows(rowList);
    }

    dispatch(resetCalulatorApiResultToInintal());
  };

  const updateAdvancedSearchFieldValues = (
    event,
    item
  ) => {
    const nextValues = cloneDeep(
      advanceSearchFieldValues
    );

    if (
      event.target.value === "" ||
      event.target.value === null
    ) {
      if (item.type === "date") {
        nextValues[item.key] = "";
      } else {
        delete nextValues[item.key];
      }
    } else {
      nextValues[item.key] = event.target.value;
    }

    setAdvanceSearchFieldValues(nextValues);
    dispatch(resetCalulatorApiResultToInintal());
  };

  const renderSelectOptions = (item) => {
    const options =
      defaultDropdownOptions?.[item.key];

    if (!options?.length) {
      return null;
    }

    return options.map((option) => (
      <option
        key={`${item.key}_${option}`}
        value={option}
      >
        {option}
      </option>
    ));
  };

  const handleSearchActionForSelectedRow = () => {
    let requestParams = cloneDeep(
      advanceSearchFieldValues
    );

    let tempKey = cloneDeep(selectedRowKeys);

    if (searchType === "material") {
      tempKey = Number(tempKey[0]);

      requestParams.ranges = cloneDeep(
        selectedColumns
      );

      const tempColumns = cloneDeep(columnsList);

      const materialRows = Array.isArray(
        tableData.data?.rows
      )
        ? tableData.data.rows
        : [];

      materialRows.forEach((filteredRow) => {
        if (filteredRow.key === tempKey) {
          tempColumns.forEach((item) => {
            if (
              filteredRow[item.dbColumnName] !==
              undefined
            ) {
              requestParams.ranges[
                item.dbColumnName
              ] = item;

              requestParams.ranges[
                item.dbColumnName
              ].target =
                filteredRow[item.dbColumnName];
            }
          });
        }
      });

      dispatch(
        fetchsearchResultsFromMaterialDB({
          advanceSearch: requestParams,
        })
      );

      setSelectedColumns(requestParams.ranges);
      setSelectedRowKeys([]);
      setSelectedRows([]);

      return;
    }

    if (searchType === "fiber") {
      const validation = getFiberMassValidation();

      if (!validation.isValid) {
        setFiberAvgError({
          code: "300",
          response: validation.message,
          type: "error",
        });

        return;
      }

      const selectedColumnsCopy =
        cloneDeep(selectedColumns);

      const request = tempKey.map(
        (item) => selectedColumnsCopy[item]
      );

      dispatch(
        fetchAverageFiberResultsFromCalculator({
          fiber: request,
        })
      ).then((response) => {
        if (response.request.status === 200) {
          setFiberAvgError({
            code: response.request.status,
            response: response.data.responseData,
            type: "success",
          });
        } else {
          setFiberAvgError({
            code: response.request.status,
            response: response.request.response,
            type: "error",
          });
        }
      }).catch((err) => {
        const detail =
          err?.response?.data?.responseDesc ||
          err?.message ||
          "Unable to calculate the average fiber. Please try again.";

        setFiberAvgError({
          code: String(err?.response?.status || 500),
          response: String(detail),
          type: "error",
        });
      });
    }
  };

  const handleSearchAction = () => {
    if (searchType === "material") {
      const requestParams = cloneDeep(
        advanceSearchFieldValues
      );

      requestParams.ranges = cloneDeep(
        selectedColumns
      );

      dispatch(
        fetchsearchResultsFromMaterialDB({
          advanceSearch: requestParams,
        })
      );

      return;
    }

    if (searchType === "sam") {
      dispatch(
        fetchsearchResultsFromSamDB({
          advanceSearch: cloneDeep(
            advanceSearchFieldValues
          ),
        })
      );

      return;
    }

    if (searchType === "fiber") {
      dispatch(
        fetchsearchResultsFromFiberDB({
          advanceSearch: cloneDeep(
            advanceSearchFieldValues
          ),
        })
      );

      return;
    }

    if (searchType === "labvantage") {
      let requestParams = cloneDeep(
        advanceSearchFieldValues
      );

      (others?.labvantage || []).forEach((item) => {
        const dynamicKey =
          item?.key ||
          item?.dbColumnName ||
          item?.fieldKey;

        if (
          dynamicKey &&
          requestParams[dynamicKey] ===
            undefined &&
          item?.value !== undefined
        ) {
          requestParams[dynamicKey] = item.value;
        }
      });

      requestParams =
        normalizeLabVantageSearchParams(
          requestParams
        );

      const normalizedTargetKey =
        normalizeSearchKey(
          props?.advancedSearchMetaData?.key
        );

      const normalizedTargetLabel =
        normalizeSearchKey(
          props?.advancedSearchMetaData?.fieldLabel
        );

      const fallbackQueryId =
        normalizedTargetKey === "moisture" ||
        normalizedTargetLabel.includes("moisture")
          ? "STM-00378 Analyzer"
          : normalizedTargetKey ===
                "gelbedpermeability" ||
            normalizedTargetLabel.includes(
              "gelbedpermeability"
            )
          ? "STM-00229 µmkPaStd"
          : normalizedTargetKey === "03psigbp" ||
              normalizedTargetLabel.includes(
                "03psigbp"
              )
          ? "STM-00220 Std2.07kPa"
          : normalizedTargetKey.includes(
                "samdensity"
              ) ||
              normalizedTargetLabel.includes(
                "samdensity"
              )
          ? DRY_GEL_DENSITY_QUERY_ID
          : null;

      const queryId =
        activeLabVantageTab?.queryId ||
        activeLabVantageTab?.labVantageApi ||
        fallbackQueryId;

      if (!queryId) {
        return;
      }

      requestParams = sanitizeRequestParams(
        requestParams
      );

      requestParams.queryId = queryId;

      setSubmittedLabVantageSearchParams(
        requestParams
      );

      setIsLabvantageSearching(true);

      dispatch(
        fetchsearchResultsFromLabvantageAPi(
          requestParams
        )
      ).finally(() => {
        setIsLabvantageSearching(false);
      });

      return;
    }

    if (searchType === "compression") {
      const requestParams = cloneDeep(
        advanceSearchFieldValues
      );

      const tempParams = {};

      Object.keys(requestParams).forEach((key) => {
        const matchingItem = (
          others?.[searchType] || []
        ).find((item) => item.key === key);

        if (matchingItem) {
          tempParams[key] = requestParams[key];
        }
      });

      dispatch(
        getCompressionData({
          advanceSearch: tempParams,
        })
      );
    }
  };

  const handleColumnSelection = (item) => {
    const nextColumns = cloneDeep(
      selectedColumns
    );

    if (
      Object.prototype.hasOwnProperty.call(
        nextColumns,
        item.dbColumnName
      )
    ) {
      delete nextColumns[item.dbColumnName];
    } else {
      nextColumns[item.dbColumnName] = {
        ...item,
        min: null,
        max: null,
        target: null,
      };
    }

    setSelectedColumns(nextColumns);
  };

  const renderColumnNames = () =>
    columnsList?.length > 0
      ? columnsList.map((item) => (
          <li
            key={item.dbColumnName}
            onClick={() =>
              handleColumnSelection(item)
            }
          >
            <Space>
              <Checkbox
                checked={Object.prototype.hasOwnProperty.call(
                  selectedColumns,
                  item.dbColumnName
                )}
              />
              <span>{item.fieldLabel}</span>
            </Space>
          </li>
        ))
: null;

  const renderMultiselectDropdown = () => (
    <div className="col-12">
      <div className="customMultiSelectContainer">
        <div className="form-group customMultiSelectGroup">
          <Typography.Text strong>
            Select Target Columns
          </Typography.Text>

          <Popover
            content={
              <div className="customMultiSelectList">
                <ul>{renderColumnNames()}</ul>
              </div>
            }
            title={
              <div style={{ width: 225 }}>
                Select Columns to set Minimum and
                Maximum Ranges
              </div>
            }
            trigger="click"
            placement="bottomRight"
            open={columnChooserVisible}
            onOpenChange={setColumnChooserVisible}
destroyTooltipOnHide
          >
            <Button>
              {Object.keys(selectedColumns).length >
              0
                ? `${
                    Object.keys(selectedColumns).length
                  } Columns selected`
                : "Choose Columns"}
</Button>
          </Popover>
        </div>
      </div>
    </div>
  );

  const updateValue = (event, key, type) => {
    const nextColumns = cloneDeep(
      selectedColumns
    );

    nextColumns[key][type] = event.target.value;
    setSelectedColumns(nextColumns);
  };

  const renderMinMaxSelection = () =>
    Object.keys(selectedColumns).length > 0
      ? Object.keys(selectedColumns).map((key) => {
          const column = selectedColumns[key];

          return (
            <div
              className="col-md-12"
              key={column.dbColumnName}
            >
              <div className="form-group minMaxInputFormGroup">
                <span className="fieldLabel">
                  {column.fieldLabel}
                </span>

                <div className="minMaxInputGroup">
                  {["min", "max", "target"].map(
                    (type) => (
                      <div
                        className="minMax"
                        key={type}
                      >
                        <label
                          htmlFor={`${getAdvanceSearchFieldId(
                            column.dbColumnName
                          )}-${type}`}
                        >
                          {type[0].toUpperCase() +
                            type.slice(1)}
                        </label>

                        <input
                          type="number"
                          id={`${getAdvanceSearchFieldId(
                            column.dbColumnName
                          )}-${type}`}
                          name={`${getAdvanceSearchFieldId(
                            column.dbColumnName
                          )}-${type}`}
                          className="form-control"
                          value={sanitizeNumberInputValue(
                            column[type]
                          )}
                          onChange={(event) =>
                            updateValue(
                              event,
                              column.dbColumnName,
                              type
                            )
                          }
                        />
                      </div>
)
                  )}
                </div>
              </div>
            </div>
          );
        })
      : null;

  const renderInputField = (item) => {
    const fieldId = getAdvanceSearchFieldId(
      item.key
    );

    if (item.type === "dropdown") {
      return (
        <select
          className="custom-select"
          id={fieldId}
          name={fieldId}
          value={
            advanceSearchFieldValues[item.key] ?? ""
          }
          onChange={(event) =>
            updateAdvancedSearchFieldValues(
              event,
              item
            )
          }
        >
          <option disabled value="">
            Choose...
          </option>

          {renderSelectOptions(item)}
        </select>
      );
    }

    const value =
      advanceSearchFieldValues[item.key] !==
      undefined
        ? advanceSearchFieldValues[item.key]
        : "";

    return (
      <input
        disabled={item.editable ? true : false}
        type={item.type}
        id={fieldId}
        name={fieldId}
        className="form-control"
        value={
          item.type === "number"
            ? sanitizeNumberInputValue(value)
            : value
        }
        onChange={(event) =>
          updateAdvancedSearchFieldValues(
            event,
            item
          )
        }
      />
    );
  };

  const renderManualInputFields = (
    currentManualInput,
    value
  ) =>
    Object.keys(currentManualInput).map(
      (itemKey) => {
        const tempValue =
          value &&
          typeof value === "object" &&
          value[itemKey] !== undefined
            ? value[itemKey]
            : "";

        const fieldId = getAdvanceSearchFieldId(
          `manual-${itemKey}`
);

        return (
          <tr
            className="form-group"
            key={itemKey}
          >
            <td>
              <label htmlFor={fieldId}>
                {itemKey}
              </label>
            </td>

            <td>
              <input
                type="number"
                id={fieldId}
                name={fieldId}
                className="form-control"
                value={sanitizeNumberInputValue(
                  tempValue
                )}
                onChange={(event) =>
                  handleManualInput(
                    event,
                    currentManualInput,
                    itemKey
                  )
                }
              />
            </td>
          </tr>
        );
      }
    );

  const renderSearchFields = () => {
    const classValue =
      searchType === "material"
        ? "col-12 pr-3"
        : "col-6 pr-1";

    if (otherSourceType === "labvantage") {
      return (others?.[searchType] || []).map(
        (item) => (
          <div
            className={classValue}
            key={item.key}
          >
            <div className="form-group">
              <label
                htmlFor={getAdvanceSearchFieldId(
                  item.key
                )}
              >
                {item.label}
              </label>

              {renderInputField(item)}
            </div>
          </div>
        )
      );
    }

    if (otherSourceType === "others") {
      const value = activeLabVantageTab?.dbColumnName
        ? labVantageInputs[
            activeLabVantageTab.dbColumnName
          ]
        : "";

      const labVantageDetail =
        queryIdForSelectedPropertyRow?.data
          ?.labVantageDetail;

      if (labVantageDetail?.showManualInput) {
        return Object.keys(
          labVantageDetail.manualInput || {}
        ).map((manualInputKey) => {
          const currentManualInput =
            labVantageDetail.manualInput[
              manualInputKey
            ];

          const tableColumns =
            manualInputKey.split("/");

          if (tableColumns[1] === undefined) {
            tableColumns[1] = "Value";
          }

          return (
            <table
              key={`manual_${manualInputKey}`}
              className="table table-borderless table-sm labVantageFieldsTable"
              style={{
                maxWidth: "700px",
                marginLeft: "20px",
              }}
            >
              <thead>
                <tr>
                  <th>{tableColumns[0]}</th>
                  <th>{tableColumns[1]}</th>
                </tr>
              </thead>

              <tbody>
                {renderManualInputFields(
                  currentManualInput,
                  value
                )}
              </tbody>
            </table>
          );
        });
      }

      return (
        <div className={classValue}>
          <div className="form-group">
            <label
              htmlFor={getAdvanceSearchFieldId(
                activeLabVantageTab?.dbColumnName,
                "labvantage-value"
              )}
            >
              {activeLabVantageTab?.fieldLabel}
            </label>

            <input
              type="number"
              id={getAdvanceSearchFieldId(
                activeLabVantageTab?.dbColumnName,
                "labvantage-value"
              )}
              name={getAdvanceSearchFieldId(
                activeLabVantageTab?.dbColumnName,
                "labvantage-value"
              )}
              className="form-control"
              value={sanitizeNumberInputValue(value)}
              onChange={(event) =>
                onSelectRowEvent(event)
              }
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const renderCalculatorFields = () => {
    if (
      queryIdForSelectedPropertyRow.loading !==
        false ||
      queryIdForSelectedPropertyRow.responseType !==
        "success" ||
      !calculatorDetail?.data?.length
    ) {
      return null;
    }

    return (
      <div
        className="col-12 columnOuterContainer"
        style={{ backgroundColor: "#fff" }}
      >
        <p className="pTitle">Others</p>

        <div className="row">
          {calculatorDetail.data.map((item) => {
            const fieldItem = {
              key: item.dbColumnName,
              type: item.type || "number",
            };

            const fieldLabel =
              item.dbColumnName === "VacuumAUL"
                ? "SAM Capacity @0.3 psi"
                : item.fieldLabel;

            const isSamDensityField =
              isSamDensityCalculatorField(item);

            return (
              <div
                className="col-md-3"
                key={item.dbColumnName}
              >
                <div className="form-group">
                  <label
                    htmlFor={getAdvanceSearchFieldId(
                      item.dbColumnName
                    )}
                  >
                    {fieldLabel}
                  </label>

                  <input
                    type="number"
                    id={getAdvanceSearchFieldId(
                      item.dbColumnName
                    )}
                    name={getAdvanceSearchFieldId(
                      item.dbColumnName
                    )}
                    className="form-control"
                    disabled={
                      !isSamDensityField &&
                      item.editable === true
                        ? true
                        : false
                    }
                    value={sanitizeNumberInputValue(
                      advanceSearchFieldValues[
                        item.dbColumnName
                      ] ?? ""
                    )}
                    onChange={(event) =>
                      updateAdvancedSearchFieldValues(
                        event,
                        fieldItem
                      )
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getCurrentFilterValues = () => {
    const filterValues = {};

    (others?.labvantage || []).forEach((field) => {
      if (
        advanceSearchFieldValues[field.key] !==
        undefined
      ) {
        filterValues[field.key] =
          advanceSearchFieldValues[field.key];
      }
    });

    return filterValues;
  };

  const handleLabVantageTabClick = (item) => {
    const previousTabKey = getLabVantageTabKey(
      activeLabVantageTab
    );

    const nextTabKey = getLabVantageTabKey(item);
    const snapshot =
      labVantageTabSnapshots[nextTabKey];

    setLabVantageTabSnapshots((previous) => ({
      ...previous,
      [previousTabKey]: {
        ...previous[previousTabKey],
        selectedRowKeys: cloneDeep(selectedRowKeys),
        selectedRows: cloneDeep(selectedRows),
        lastLabvSelection: cloneDeep(
          lastLabvSelection
        ),
        labVantageInputs: cloneDeep(
          labVantageInputs
        ),
        tabValue: cloneDeep(
          getCurrentTabValue(activeLabVantageTab)
        ),
        tableData: cloneDeep(tableData),
      },
    }));

    setLabVantageActiveTab(item);
    setSelectedApiTab(item);
    setSelectedColumns({});

    if (snapshot) {
      setSelectedRowKeys(
        snapshot.selectedRowKeys || []
      );

      setSelectedRows(snapshot.selectedRows || []);

      setLastLabvSelection(
        snapshot.lastLabvSelection || null
      );

      if (snapshot.labVantageInputs) {
        setLabVantageInputs(
          cloneDeep(snapshot.labVantageInputs)
        );
      }
    } else {
      setSelectedRowKeys([]);
      setSelectedRows([]);
      setLastLabvSelection(null);
    }

    if (
      searchType === "labvantage" &&
      otherSourceType === "labvantage"
    ) {
      // Use current (global) filter values - they are constant across all tabs
      // Never use snapshot's filters as those are stale from previous tab state
      const hasActiveFilters = submittedLabVantageSearchParams &&
        Object.keys(submittedLabVantageSearchParams).length > 0;
      
      let requestParams = hasActiveFilters
        ? cloneDeep(submittedLabVantageSearchParams)
        : cloneDeep(advanceSearchFieldValues);

      (others?.labvantage || []).forEach((field) => {
        const dynamicKey =
          field?.key ||
          field?.dbColumnName ||
          field?.fieldKey;

        if (
          dynamicKey &&
          requestParams[dynamicKey] ===
            undefined &&
          field?.value !== undefined
        ) {
          requestParams[dynamicKey] = field.value;
        }
      });

      requestParams =
        normalizeLabVantageSearchParams(
          requestParams
        );

      const queryId =
        item?.queryId || item?.labVantageApi;

      if (queryId) {
        requestParams.queryId = queryId;

        setSubmittedLabVantageSearchParams(
          requestParams
        );

        setIsLabvantageSearching(true);

        dispatch(
          fetchsearchResultsFromLabvantageAPi(
            requestParams
          )
        ).finally(() => {
          setIsLabvantageSearching(false);
        });
      }
    }
  };

  const renderLabvantageFieldsAsTabs = () => {
    const labVantageDetail =
      queryIdForSelectedPropertyRow.data
        ?.labVantageDetail;

    if (
      queryIdForSelectedPropertyRow.loading !==
        false ||
      queryIdForSelectedPropertyRow.responseType !==
        "success" ||
      labVantageDetail === undefined
    ) {
      return null;
    }

    const tabsData = (
      labVantageDetail.data || []
    ).filter(
      (item) =>
        !isSamDensityCalculatorField(item) &&
        !isDryGelDensityQuery(
          item.queryId || item.labVantageApi
        )
    );

    return (
      <div
        className="labvantageTabTable"
        style={{
          width: "100%",
          border: "1px solid #d9d9d9",
          backgroundColor: "#fff",
        }}
      >
        <div
          className="labvantageTabHeader"
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(0, 1fr) minmax(120px, 0.8fr)",
            borderBottom: "1px solid #d9d9d9",
            fontWeight: 600,
            padding: "8px 10px",
          }}
        >
          <div>API</div>
          <div>Value</div>
        </div>

        {tabsData.map((item) => {
          const tabKey = getLabVantageTabKey(item);

          const isActive =
            tabKey ===
            getLabVantageTabKey(
              activeLabVantageTab
            );

          const hasTabError =
            labVantageTabErrors[tabKey] === true;

          return (
            <button
              type="button"
              key={tabKey}
              className={`lvRow ${
                isActive ? "activeItem" : ""
              } ${hasTabError ? "errorItem" : ""}`}
              onClick={() =>
                handleLabVantageTabClick(item)
              }
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(0, 1fr) minmax(120px, 0.8fr)",
                width: "100%",
                border: "0",
                borderBottom: "1px solid #eeeeee",
                textAlign: "left",
                padding: "9px 10px",
                cursor: "pointer",
                backgroundColor: hasTabError
                  ? "#dc3545"
                  : isActive
                  ? "#1677ff"
                  : "#fff",
                color:
                  hasTabError || isActive
                    ? "#fff"
                    : "#222",
              }}
            >
              <span className="lvCol">
                {item.fieldLabel ||
                  item.labVantageApi ||
                  item.queryId ||
                  item.dbColumnName}
              </span>

              <span className="lvCol">
                {formatTabValue(
                  getCurrentTabValue(item)
                )}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderMetaData = () =>
    Object.keys(
      defaultMetaDataFieldsList?.data?.fields || {}
    ).map((itemKey) => {
      const currentField =
        defaultMetaDataFieldsList.data.fields[
          itemKey
        ];

      if (currentField.render !== true) {
        return null;
      }

      const fieldId = getAdvanceSearchFieldId(
        `metadata-${itemKey}`
      );

      return (
        <tr key={itemKey}>
          <td>
            <label htmlFor={fieldId}>
              {currentField.label}
            </label>
          </td>

          <td>
            {currentField.type === "textarea" ? (
              <textarea
                id={fieldId}
                name={fieldId}
                className="form-control"
                value={getActiveManualMetadata()[itemKey] ?? ""}
                onChange={(event) => updateManualMetadata(event, itemKey)}
                readOnly={
                  currentField.editable === false
                }
                style={{
                  background: "#fff",
                  minWidth: "100%",
                }}
              />
            ) : (
              <input
                type={currentField.type}
                id={fieldId}
                name={fieldId}
                className="form-control"
                value={getActiveManualMetadata()[itemKey] ?? ""}
                onChange={(event) => updateManualMetadata(event, itemKey)}
                readOnly={
                  currentField.editable === false
                }
              />
            )}
          </td>
        </tr>
      );
    });

  const handleOtherSourceEvent = (event) => {
    selectionRecordRef.current = null;
    delete selectedInputMetadataRef.current[getLabVantageTabKey(activeLabVantageTab)];
    setOtherSourceType(event.target.value);
    setSelectedColumns({});
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setLastLabvSelection(null);
    setSubmittedLabVantageSearchParams({});

    dispatch(resetMaterailSearchReucer({}));

    if (activeLabVantageTab?.dbColumnName) {
      setLabVantageInputs((previous) => ({
        ...previous,
        [activeLabVantageTab.dbColumnName]: null,
      }));

      updateStoredLabVantageTabValue(
        activeLabVantageTab,
        null
      );
    }

    dispatch(resetCalulatorApiResultToInintal());
  };

  const renderLabvantageFieldsForCalc = () => {
    if (props.modal.calulatorForm !== true) {
      return null;
    }

    return (
      <div className="col-12 labv">
        <div
          className="labvantageCalcPanels"
          style={{
            display: "flex",
            alignItems: "stretch",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div
            className="labvantageTabPanel"
            style={{
              flex: "0 0 38%",
              minWidth: "280px",
            }}
          >
            {renderLabvantageFieldsAsTabs()}
          </div>

          <div
            className="labvantageFilterPanel bordered-container"
            style={{
              flex: "1 1 58%",
              minWidth: "320px",
            }}
          >
            <div className="row">
              <div className="col-12 labvantageContainer">
                <div className="form-group select-flex">
                  <label htmlFor="advance-search-source">
                    <b>Source</b>
                  </label>

                  <select
                    id="advance-search-source"
                    name="advance-search-source"
                    className="custom-select"
                    value={otherSourceType}
                    onChange={handleOtherSourceEvent}
                  >
                    <option value="labvantage">
                      {props.modal.module !==
                      "compression"
                        ? "Labvantage"
                        : "Compression"}
                    </option>

                    {props.modal.module !==
                    "compression" ? (
                      <option value="others">
                        Others
                      </option>
                    ) : null}
                  </select>
                </div>
              </div>

              <div className="col-12">
                <div className="row">
                  {renderSearchFields()}
                </div>
              </div>

              {otherSourceType === "others" ? (
                <div className="col-12">
                  <br />

                  <p>
                    <b>Meta Data for Input</b>
                  </p>

                  <table className="table table-bordered table-sm labVantageFieldsTable">
                    <tbody>
                      {renderMetaData()}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div
          className="labvantageOthersRow"
          style={{
            width: "100%",
            marginTop: "12px",
          }}
        >
          {renderCalculatorFields()}
        </div>
      </div>
    );
  };

  const renderSearchContent = () => {
    if (props.modal.calulatorForm === false) {
      const classValue =
        searchType === "material"
          ? "col-5"
          : "col-12";

      return (
        <div className="col-12">
          <div className="row">
            <div className={classValue}>
              <div className="row">
                {renderSearchFields()}
              </div>
            </div>

            {searchType === "material" ? (
              <div className="col-7">
                <div className="row">
                  {renderMultiselectDropdown()}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className="col-12">
        <div className="row">
          {!autoCallCalulator ? (
            <div className="col-12">
              <div className="calulcationParameters">
                <b>Calculation Parameters</b>
              </div>
            </div>
          ) : null}

          <div className="col-12">
            <div className="row">
              {queryIdForSelectedPropertyRow.data
                ?.labVantageDetail !== undefined ? (
                <div
                  className="col-12 columnOuterContainer"
                  style={{ backgroundColor: "#fff" }}
                >
                  <p className="pTitle">
                    <span>
                      Inputs from other sources
                    </span>
                  </p>

                  {renderLabvantageFieldsForCalc()}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSearchType = () => {
    if (
      searchType !== "labvantage" &&
      searchType !== "compression" &&
      searchType !== "graphVisualizer"
    ) {
      if (
        urlParams.moduleName ===
          "material-details" ||
        urlParams.moduleName === "new-material"
      ) {
        return (
          <div className="col-12">
            <div className="form-group searchType">
              <select
                className="custom-select"
                id="searchType"
                name="searchType"
                value={searchType}
                onChange={
                  handleSearchTypeChangeEvent
                }
              >
                <option disabled>
                  Choose search type...
                </option>

                <option value="material">
                  Material
                </option>

                <option value="sam">SAM</option>
                <option value="fiber">Fiber</option>
              </select>
            </div>
          </div>
        );
      }

      return null;
    }

    if (searchType === "labvantage") {
      const currentTestMethod =
        activeLabVantageTab?.fieldLabel ||
        queryIdForSelectedPropertyRow.data
          ?.labVantageDetail?.data?.[0]?.fieldLabel ||
        "-";

      return (
        <div className="col-12">
          <div className="form-group searchType">
            <strong>Test Method: </strong>
            {currentTestMethod}
          </div>
        </div>
      );
    }

    if (searchType === "graphVisualizer") {
      return (
        <Graphs
          dataSet={props.moduleDefaultFieldDataSet}
          fields={props.fields}
        />
      );
    }

    return null;
  };

  const renderSearchActionButtonContainer = () => {
    if (
      props.modal.calulatorForm === false &&
      otherSourceType === "labvantage"
    ) {
      return (
        <div className="col-md-12">
          {searchType !== "graphVisualizer" ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={isLabvantageSearching}
              onClick={handleSearchAction}
            >
              {isLabvantageSearching
                ? "Searching..."
                : `Search ${searchType}`}
            </button>
          ) : null}

          <hr />
        </div>
      );
    }

    if (
      props.modal.calulatorForm === true &&
      otherSourceType === "labvantage"
    ) {
      const hasQueryId =
        activeLabVantageTab?.queryId ||
        activeLabVantageTab?.labVantageApi;

      const hasInputFields =
        Object.keys(advanceSearchFieldValues)
          .length > 0;

      return (
        <div className="col-md-12">
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              (!hasQueryId && !hasInputFields) ||
              isLabvantageSearching
            }
            onClick={handleSearchAction}
          >
            {isLabvantageSearching
              ? "Searching..."
              : `Search ${searchType}`}
          </button>

          <hr />
        </div>
      );
    }

    return null;
  };

  const renderGridContent = () => {
    if (isLabvantageSearching) {
      return (
        <div className="row sideBarContent">
          <div
            className="col-12"
            style={{
              textAlign: "center",
              padding: "24px 0",
            }}
          >
            <img
              src={loader}
              className="loaderr"
              alt="loading"
            />

            <p>Searching...</p>
          </div>
        </div>
      );
    }

    const activeTabSnapshot =
      searchType === "labvantage" &&
      activeLabVantageTab
        ? labVantageTabSnapshots[
            getLabVantageTabKey(
              activeLabVantageTab
            )
          ]
        : null;

    const currentTableData =
      activeTabSnapshot?.tableData || tableData;

    if (
      currentTableData.loading !== false ||
      currentTableData.responseType !== "success"
    ) {
      return null;
    }

    const originalRows = Array.isArray(
      currentTableData.data?.rows
    )
      ? currentTableData.data.rows
      : [];

    const displayedRows =
      searchType === "labvantage"
        ? filterLabVantageRows(
            originalRows,
            submittedLabVantageSearchParams
          )
        : originalRows;

    const displayedTableData = {
      ...currentTableData,
      data: {
        ...currentTableData.data,
        rows: displayedRows,
      },
    };

    if (displayedRows.length === 0) {
      return (
        <div className="row sideBarContent">
          <div
            className="col-12"
            style={{
              textAlign: "center",
              padding: "24px 0",
            }}
          >
            <p>
              No data found for the given search
              criteria.
            </p>
          </div>
        </div>
      );
    }

    const displayRowSelection = {
      ...rowSelectionProps,
      type:
        searchType === "fiber"
          ? "checkbox"
          : "radio",
    };

    return (
      <div className="row sideBarContent">
        <DataGrid
          tableData={displayedTableData}
          path="material-details"
          enableRowSelection
          rowSelection={displayRowSelection}
          onSelectRowEvent={onSelectRowEvent}
        />
      </div>
    );
  };

  const renderFooterButtons = () => {
    // Hide Use Selection for immediate-apply LabVantage calculator forms:
    // sample-based (GelProp_C/CalcDiff) and multi-field (GelProp_A/GelProp_B/SAMRadius).
    // Value-based calculators (like Gel Bed Permeability) should show the Use Selection button.
    const isLabvantageCalculatorModal =
      searchType === "labvantage" &&
      props.modal.calulatorForm === true &&
      (isSampleBasedCalculator === true ||
        isHookesLawCalculator === true);

    const showUseSelection =
      !isLabvantageCalculatorModal &&
      showLabvantageInputs === false &&
      showLabVantageCalculatorButton === false &&
      autoCallCalulator === false &&
      (searchType === "fiber"
        ? selectedRowKeys.length === 1
        : selectedRowKeys.length >= 1 ||
          selectedRows.length >= 1 ||
          Boolean(lastLabvSelection));

    return (
      <div className="row sideBarContent">
        <div
          className="fixedbottom sl1"
          style={{
            position: "fixed",
            bottom: "5px",
            display: "block",
          }}
        >
          {searchType === "fiber" &&
          selectedRowKeys.length >= 2 &&
          selectedRowKeys.length <= 3 ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={
                handleSearchActionForSelectedRow
              }
            >
              Calculate Average
            </button>
          ) : null}

          {searchType === "fiber" &&
          selectedRowKeys.length >= 2 &&
          selectedRowKeys.length <= 3 ? (
            <button
              type="button"
              className="btn btn-success"
              disabled={
                fiberAvgError.type !== "success"
              }
              onClick={handleUseAvgFiberEvent}
            >
              Use Calculated Average
            </button>
          ) : null}

          {searchType === "material" ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={
                handleSearchActionForSelectedRow
              }
            >
              Set as Target
            </button>
          ) : null}

          {showUseSelection ? (
            <button
              type="button"
              className="btn btn-success"
              onClick={handleUseSectionEvent}
            >
              Use Selection
            </button>
          ) : null}

          {(searchType === "labvantage" ||
            searchType === "compression") &&
          calulatorApiResultData.responseType ===
            "success" &&
          calulatorApiResultData.loading === false ? (
            <button
              type="button"
              className="btn btn-success"
              onClick={
                handleUseSectionEventForCalcResults
              }
            >
              Use Calculator Output
            </button>
          ) : null}

          {(searchType === "labvantage" ||
            searchType === "compression") &&
          props.modal.calulatorForm === true &&
          calculatorDetail?.calculatorName ? (
            <button
              type="button"
              className="btn btn-success"
              disabled={isCalculatorRunning}
              aria-busy={isCalculatorRunning}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
              onClick={handleRunCalcEvent}
            >
              {isCalculatorRunning ? (
                <>
                  <img
                    src={loader}
                    className="loaderr mr-1"
                    alt=""
                  />
                  Running Calculator…
                </>
              ) : (
                "Run Calculator"
              )}
            </button>
          ) : null}

          <button
            type="button"
            className="btn btn-secondary"
            id="cancelALL"
            onClick={() =>
              props.closeAdvancedSearchModal(false)
            }
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderCalculatorResult = () => {
    if (
      calulatorApiResultData.loading !== false ||
      calulatorApiResultData.responseType !== "success"
    ) {
      return null;
    }

    const outputData = Array.isArray(
      calulatorApiResultData.data?.outputData
    )
      ? calulatorApiResultData.data.outputData
      : [];

    if (outputData.length === 0) {
      return (
        <div
          className="col-12"
          style={{ textAlign: "center" }}
        >
          <p>No results returned</p>
        </div>
      );
    }

    return (
      <table className="table table-bordered table-sm labVantageFieldsTable">
        <thead>
          <tr>
            <th>Property</th>
            <th>Value</th>
          </tr>
        </thead>

        <tbody>
          {outputData.map((item, index) => (
            <tr
              key={`${item.fieldLabel}_${index}`}
            >
              <td>{item.fieldLabel}</td>
              <td>{item.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const handleFiberMassPercentChangeEvent = (
    event,
    key
  ) => {
    setFiberAvgError({
      code: "",
      response: "",
      type: "",
    });

    const nextColumns = cloneDeep(selectedColumns);

    nextColumns[key] = {
      id: key,
      percentage:
        event.target.value === ""
          ? ""
          : Number(event.target.value),
    };

    setSelectedColumns(nextColumns);
  };

  const getFiberMassValidation = () => {
    const values = selectedRowKeys.map(
      (key) => selectedColumns[key]?.percentage
    );

    const entered = values.filter(
      (value) => value !== undefined && value !== ""
    );

    const allEntered =
      values.length > 0 &&
      entered.length === values.length;

    const sum = entered.reduce(
      (total, value) => total + Number(value),
      0
    );

    const outOfRange = entered.some(
      (value) =>
        Number.isNaN(Number(value)) ||
        Number(value) < 0 ||
        Number(value) > 100
    );

    let validationMessage = "";

    if (outOfRange) {
      validationMessage =
        "Mass % of each fiber should be between 0 and 100";
    } else if (Math.abs(sum - 100) > 1e-6) {
      validationMessage =
        "Sum of percentages of Mass should be equal to 100";
    } else if (!allEntered) {
      validationMessage =
        "Enter Mass % for all the selected fibers";
    }

    return {
      anyEntered: entered.length > 0,
      allEntered,
      sum: Number(sum.toFixed(4)),
      message: validationMessage,
      isValid:
        selectedRowKeys.length >= 2 &&
        selectedRowKeys.length <= 3 &&
        allEntered &&
        validationMessage === "",
    };
  };

  const renderFiberHelpContent = () => {
    if (
      tableData.loading === false &&
      tableData.responseType === "success" &&
      selectedRowKeys.length < 2
    ) {
      return (
        <div className="col-12">
          <p>
            <b>Average Fiber</b>
            <br />
            <small className="textNormal">
              Select minimum two and maximum three
              rows from the above table for average
              fiber calulation.
            </small>
          </p>
        </div>
      );
    }

    if (
      selectedRowKeys.length > 1 &&
      selectedRowKeys.length <= 3
    ) {
      return (
        <div className="col-12">
          <b>Average Fiber</b>
          <br />
          <small className="textNormal">
            Add Mass % for the selected fibers and
            click on Calculate Average button
          </small>
        </div>
      );
    }

    return null;
  };

  const renderAverageFiber = () => {
    if (
      selectedRowKeys.length < 2 ||
      selectedRowKeys.length > 3 ||
      !Array.isArray(tableData.data?.rows)
    ) {
      return null;
    }

    const columnClass =
      selectedRowKeys.length === 3
        ? "fiberMP col-4"
        : "fiberMP col-6";

    const validation = getFiberMassValidation();

    return (
      <>
        {tableData.data.rows
          .filter(
            (row) =>
              selectedRowKeys.indexOf(row.key) !== -1
          )
          .map((row) => (
            <div
              className={columnClass}
              key={`fiber_mass_${row.key}`}
            >
              <div className="form-group">
                <label>
                  {row.key}-{row.fiberName}
                </label>

                <input
                  type="number"
                  min="0"
                  max="100"
                  step="any"
                  id={`fiber_mass_${row.key}`}
                  className="form-control"
                  value={sanitizeNumberInputValue(
                    selectedColumns[row.key] !== undefined
                      ? selectedColumns[row.key].percentage
                      : ""
                  )}
                  onChange={(event) =>
                    handleFiberMassPercentChangeEvent(
                      event,
                      row.key
                    )
                  }
                />
                %
              </div>
            </div>
          ))}

        {validation.allEntered &&
        !validation.isValid &&
        fiberAvgError.type !== "error" ? (
          <div className="col-12">
            <p style={{ color: "red" }}>
              <b>Error</b>
              <br />
              {validation.message}
            </p>
          </div>
        ) : null}
      </>
    );
  };

  const renderAverageFiberTable = () => {
    if (
      fiberAvgError.type === "success" &&
      fiberAvgError.response !== ""
    ) {
      return (
        <DataGrid
          tableData={{ data: fiberAvgError.response }}
          path="material-details"
          enableRowSelection={false}
        />
      );
    }

    if (fiberAvgError.type === "error") {
      return (
        <div className="col-12">
          <p style={{ color: "red" }}>
            <b>Error</b>
            <br />
            {fiberAvgError.response}
          </p>
        </div>
      );
    }

    return null;
  };

  const renderCalculatorError = () => {
    if (!calculatorRunError) {
      return null;
    }

    return (
      <div
        className="col-12"
        style={{ textAlign: "center" }}
      >
        <p style={{ color: "red" }}>
          Calculator error
        </p>
        <p>{calculatorRunError}</p>
      </div>
    );
  };

  return (
    <div
      id="defaultAPICall"
      style={{ display: "block" }}
    >
      <div className="backShade"></div>

      <div className="mysidebar larger paddingBottom">
        <div className="row">
          <div className="col-12 sidebarHeader">
            <h5>
              <i className={props.modal.icon}></i>
              &nbsp;
              {props.modal.title}
            </h5>

            <button
              type="button"
              className="close"
              onClick={() =>
                props.closeAdvancedSearchModal(false)
              }
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>

        <div className="row sideBarContent">
          {renderSearchType()}
          {renderSearchContent()}
          {renderSearchActionButtonContainer()}
        </div>

        {renderGridContent()}

        {searchType === "fiber" ? (
          <div className="row pl-3 pr-3">
            {renderFiberHelpContent()}
            {renderAverageFiber()}
            {renderAverageFiberTable()}
          </div>
        ) : null}

        {(searchType === "labvantage" ||
          searchType === "compression") &&
        props.modal.calulatorForm === true ? (
          <div className="row pl-3 pr-3">
            {renderCalculatorError()}

            <div className="col-5">
              {renderCalculatorResult()}
            </div>

            <div className="col-7">
              {calulatorApiResultData.data
                ?.outputChart ? (
                <BarChart
                  data={calulatorApiResultData.data}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        {renderFooterButtons()}
      </div>
    </div>
  );
}

export default AdvanceSearch;
